import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { MessageCircle, TrendingUp, Play, Send, BarChart3, Activity, Sparkles, Bot, User, ChevronRight, Loader2, DollarSign, Target, Calendar, X, Maximize2, Minimize, Settings, GripVertical, FileText, LogOut, Check, Plus, Tag } from 'lucide-react';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import DataTable from '../components/DataTable';
import AssetGrid from '../components/AssetGrid';
import DashboardConfig from '../components/DashboardConfig';
import SmartVisualization from '../components/SmartVisualization';
import VisualizationSelector from '../components/VisualizationSelector';
import MainDashboard from '../components/MainDashboard';
import {
  CopyThemeAnalysis,
  CreativeBreakdown,
  CreativeAttributes,
  AudienceCreative,
  TimeTrends,
  PlatformPlacement
} from '../components/dashboards';
import { useAuth } from '../contexts/AuthContext';
import dashboardService from '../services/dashboardService';

// Report type display names mapping
const REPORT_TYPE_NAMES = {
  'top-performing': 'Top Performing',
  'ad-type-analysis': 'Ad Type Analysis',
  'copy-theme-analysis': 'Hook Analysis',
  'creative-attributes': 'Creative Analysis',
  'creative-breakdown': 'Product Breakdown',
  'media-library': 'Media Library',
  // Group-by options
  'asset-id': 'Asset ID',
  'campaign-name': 'Campaign Name',
  'hook': 'Hook',
  'ad-type': 'Ad Type',
  'value-proposition': 'Value Proposition',
  'content-category': 'Content Category',
  'talent-quantity': 'Number of Talent',
  'talent-gender': 'Male vs Female',
  'talent-age': 'Age Range',
  'setting': 'Setting',
  'music-playing': 'Music Playing (Y/N)',
  'music-genre': 'Music Genre',
  'voiceover': 'Voiceover (Y/N)',
  'dominant-color': 'Dominant Color',
  'video-length': 'Video Length'
};

const Dashboard = () => {
  const { isAuthenticated, getAccountId, getAccountDisplayName, logout } = useAuth();
  const navigate = useNavigate();
  
  // Clear today's cache on component mount for testing
  useEffect(() => {
    const clearTodayCache = async () => {
      const accountId = getAccountId();
      if (!accountId) return;
      
      try {
        const response = await fetch('/api/cache/clear', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accountId,
            todayOnly: true
          })
        });
        
        const result = await response.json();
        if (result.success) {
          console.log(`✅ Cleared ${result.deleted_count} cache entries for today`);
        }
      } catch (error) {
        console.error('Error clearing cache:', error);
      }
    };
    
    // Clear cache on mount
    clearTodayCache();
  }, [getAccountId]);

  // Check if trend analytics data exists
  useEffect(() => {
    const checkTrendAnalyticsData = async () => {
      const accountId = getAccountId();
      if (!accountId) return;
      
      try {
        // Make a lightweight check to see if there's any pattern data
        const response = await fetch(`/api/trend-analytics/check-data/${encodeURIComponent(accountId)}`);
        const result = await response.json();
        if (result.success && result.hasData) {
          setHasTrendAnalyticsData(true);
        } else {
          setHasTrendAnalyticsData(false);
        }
      } catch (error) {
        console.error('Error checking trend analytics data:', error);
        setHasTrendAnalyticsData(false);
      }
    };
    
    checkTrendAnalyticsData();
  }, [getAccountId]);
  
  // Helper function to get week number
  const getWeekNumber = (date) => {
    try {
      // Handle PostgreSQL date format properly
      let d;
      if (typeof date === 'string') {
        // PostgreSQL returns dates like "2025-01-02" - parse directly
        d = new Date(date);
      } else {
        d = new Date(date);
      }
      
      // Validate the date
      if (isNaN(d.getTime())) {
        console.error('Invalid date for week calculation:', date);
        return 'Invalid';
      }
      
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() + 4 - (d.getDay() || 7));
      const yearStart = new Date(d.getFullYear(), 0, 1);
      const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
      return weekNo;
    } catch (error) {
      console.error('Error calculating week number for date:', date, error);
      return 'Error';
    }
  };
  const [chatMessages, setChatMessages] = useState([
    { id: 1, role: 'assistant', content: 'Click any prompt below to review creative insights:', 
      suggestedPrompts: [
        { text: 'top 10 ads by impressions with CTR analysis', description: 'High-reach content' },
        { text: 'top positive correlated audio features with ROAS in last 7 days', description: 'Audio-ROAS insights' },
        { text: 'top positive correlated visual features with spend in last 30 days', description: 'Visual-spend insights' }
      ]
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isTextboxExpanded, setIsTextboxExpanded] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [sectionLoading, setSectionLoading] = useState({
    metrics: true,
    trends: true,
    adTypes: true,
    highestSpendingFeatures: true,
    topContent: true
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customDays, setCustomDays] = useState(30);
  const [selectedRange, setSelectedRange] = useState(7);
  const [startDate, setStartDate] = useState('');
  const [correlationMetric, setCorrelationMetric] = useState('roas');
  const [endDate, setEndDate] = useState('');
  const [dateRangeType, setDateRangeType] = useState('preset'); // 'preset' or 'custom'
  
  // Refresh trigger - increment this to force refresh all dashboards
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Ad name filter state
  const [adNameFilter, setAdNameFilter] = useState({
    keywords: [],
    logic: 'AND', // 'OR' or 'AND'
    mode: 'include' // 'include' or 'exclude'
  });
  
  // Pending ad name filter changes (not applied until confirmed)
  const [pendingAdNameFilter, setPendingAdNameFilter] = useState({
    keywords: [''],
    logic: 'AND',
    mode: 'include'
  });
  
  // Tab state for configuration modal
  const [activeTab, setActiveTab] = useState('dateRange');
  const [chatSize, setChatSize] = useState('small'); // 'collapsed', 'small', 'large', 'custom'
  const [customChatWidth, setCustomChatWidth] = useState(400); // Custom width in pixels
  const [isResizing, setIsResizing] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    performanceData: [],
    topContent: [],
    adTypes: [],
    fastGrowingFeatures: [],
    isCustomTopContent: false, // Track if top content comes from custom query
    metrics: {
      totalImpressions: '0',
      totalSpend: '$0',
      totalClicks: '0',
      totalConversions: '0',
      impressionsGrowth: '+0%',
      spendGrowth: '+0%',
      clicksGrowth: '+0%',
      conversionsGrowth: '+0%'
    }
  });
  const [dataLoaded, setDataLoaded] = useState(false);
  const [hasAutoLoadedReport, setHasAutoLoadedReport] = useState(false);
  const autoLoadAttemptedRef = useRef(false); // Prevent double auto-load in React Strict Mode
  const [hasTrendAnalyticsData, setHasTrendAnalyticsData] = useState(false);
  const [dashboardLoadingProgress, setDashboardLoadingProgress] = useState(0);
  const [chatLoadingProgress, setChatLoadingProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [dashboardConfig, setDashboardConfig] = useState({
    metrics: {
      totalImpressions: true,
      totalSpend: true,
      totalClicks: true,
      totalConversions: true,
      totalRevenue: true,
      avgCTR: true,
      avgCPC: true,
      avgROAS: true,
      avgCPA: true,
      avgCVR: true
      // Removed reach and frequency as they don't exist in the database
    }
  });
  const messagesEndRef = useRef(null);
  const settingsIconRef = useRef(null);
  const contentQueryRef = useRef(null);

  // Ad Type Table Configuration
  const [showAdTypeConfig, setShowAdTypeConfig] = useState(false);
  const [showFeaturesConfig, setShowFeaturesConfig] = useState(false);
  const [showContentConfig, setShowContentConfig] = useState(false);
  const [adTypeTableConfig, setAdTypeTableConfig] = useState({
    viewMode: 'table', // 'cards', 'table'
    showMetrics: ['spend', 'impressions', 'ctr', 'roas'], // which metrics to display
    maxItems: 4, // number of items to show
    groupBy: 'ad_type' // 'ad_type', 'performance_tier'
  });
  const [featuresTableConfig, setFeaturesTableConfig] = useState({
    viewMode: 'table', // 'cards', 'table'
    showMetrics: ['count', 'spend', 'impressions', 'ctr', 'roas'], // which metrics to display
    maxItems: 4, // number of items to show
    groupBy: 'feature' // 'feature', 'performance_tier'
  });
  const [contentTableConfig, setContentTableConfig] = useState({
    viewMode: 'table', // 'cards', 'table'
    maxItems: 4, // number of items to show
    groupBy: 'title' // 'title', 'performance_tier'
  });
  
  // Track if top performing section has been configured by user
  const [isTopPerformingConfigured, setIsTopPerformingConfigured] = useState(false);
  // Track the configured query to use as title when section is configured
  const [configuredTopContentQuery, setConfiguredTopContentQuery] = useState('');
  // Track if user is currently in configuration process to prevent timing issues
  const [isConfiguringTopContent, setIsConfiguringTopContent] = useState(false);
  
  // Dashboard tab state
  const [activeDashboardTab, setActiveDashboardTab] = useState('top-performing');
  
  // Saved reports state
  const [savedReports, setSavedReports] = useState([]);
  const [showSaveReportModal, setShowSaveReportModal] = useState(false);
  const [newReportName, setNewReportName] = useState('');
  const [showSavedReportsList, setShowSavedReportsList] = useState(false);
  const [loadingReportId, setLoadingReportId] = useState(null);

  // Sort state for different sections
  const [adTypeSort, setAdTypeSort] = useState({ field: 'spend', order: 'desc' });
  const [featuresSort, setFeaturesSort] = useState({ field: 'spend', order: 'desc' });
  const [contentSort, setContentSort] = useState({ field: 'spend', order: 'desc' });
  
  // Chart hiding state
  const [hiddenCharts, setHiddenCharts] = useState(new Set());

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Force re-render when sorting changes to ensure table updates
  useEffect(() => {
    console.log('🔄 Sorting changed:', contentSort);
  }, [contentSort]);

  // Handle initial dynamic sorting for custom queries (only when data changes)
  useEffect(() => {
    if (dashboardData.topContent && dashboardData.topContent.length > 0 && dashboardData.isCustomTopContent) {
      // Only set initial sorting if we don't have a valid sort field
      const dynamicTable = createDynamicTable(dashboardData.topContent);
      const currentSortFieldExists = dynamicTable.columns.some(col => col.key === contentSort.field);
      
      if (!currentSortFieldExists) {
        // No dynamic sorting - only use user-configured settings
        // Dynamic behavior disabled - users must configure through settings
      }
    }
  }, [dashboardData.topContent, dashboardData.isCustomTopContent]);

  // Add navigation protection - only after initial load
  useEffect(() => {
    // Don't show warnings during initial loading
    // if (isLoading) return; // Removed isLoading check

    const handleBeforeUnload = (e) => {
      // Show confirmation dialog when user tries to leave the page or refresh
      e.preventDefault();
      e.returnValue = 'Are you sure you want to leave? Your dashboard data and chat history will be lost.';
      return e.returnValue;
    };

    // Handle browser back/forward buttons
    const handlePopState = (e) => {
      e.preventDefault();
      const confirmed = window.confirm('Are you sure you want to leave? Your dashboard data and chat history will be lost.');
      if (!confirmed) {
        // Push the current state back to prevent navigation
        window.history.pushState(null, '', window.location.href);
      }
    };

      // Handle keyboard shortcuts for refresh
  const handleKeyDown = (e) => {
    // Handle F5 refresh with confirmation
    if (e.key === 'F5') {
      e.preventDefault();
      const confirmed = window.confirm('Are you sure you want to refresh the page? Your dashboard data and chat history will be lost.');
      if (confirmed) {
        window.location.reload();
      }
      return false;
    }
    
    // Handle Ctrl+R (Windows) or Cmd+R (Mac) refresh with confirmation
    if ((e.ctrlKey || e.metaKey) && e.key === 'r') {
      e.preventDefault();
      const confirmed = window.confirm('Are you sure you want to refresh the page? Your dashboard data and chat history will be lost.');
      if (confirmed) {
        window.location.reload();
      }
      return false;
    }

    // Date picker shortcuts
    if (showDatePicker) {
      if (e.key === 'Escape') {
        setShowDatePicker(false);
      }
      if (e.key === 'Enter' && (startDate && endDate)) {
        handleCustomDateRangeSubmit();
      }
    }
  };

    // Push initial state to enable popstate detection
    window.history.pushState(null, '', window.location.href);

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []); // Removed isLoading from dependency array

  // Chat window resize handlers - Optimized for maximum responsiveness
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    // Don't disable pointer events to maintain responsiveness
  }, []);

  // Ultra-responsive resize handler with performance optimization
  const handleResizeMove = useCallback((e) => {
    if (!isResizing) return;
    
    // Calculate new width directly without any throttling
    const windowWidth = window.innerWidth;
    const newWidth = windowWidth - e.clientX;
    
    // Constrain width between 300px and 80% of screen width
    const minWidth = 300;
    const maxWidth = windowWidth * 0.8;
    
    // Update immediately for maximum responsiveness
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setCustomChatWidth(newWidth);
      setChatSize('custom');
    }
  }, [isResizing]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Ultra-responsive global mouse event listeners
  useEffect(() => {
    if (isResizing) {
      const handleMouseMove = (e) => {
        e.preventDefault();
        handleResizeMove(e);
      };
      
      const handleMouseUp = (e) => {
        e.preventDefault();
        handleResizeEnd();
      };
      
      // Use the most responsive event handling approach
      document.addEventListener('mousemove', handleMouseMove, { 
        passive: false, 
        capture: true,
        once: false
      });
      document.addEventListener('mouseup', handleMouseUp, { 
        passive: false, 
        capture: true,
        once: false
      });
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove, { capture: true });
        document.removeEventListener('mouseup', handleMouseUp, { capture: true });
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Helper function to format large numbers
  const formatNumber = (num) => {
    if (!num || num === 0) return '0';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return '$0';
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K`;
    }
    return `$${amount.toFixed(0)}`;
  };

  // Sort function for table data - now works with any field names and formatted values
  const sortData = (data, sortConfig) => {
    if (!data || data.length === 0) return data;
    
    console.log('🔍 Sorting data called:', { 
      dataLength: data.length, 
      sortConfig, 
      sampleData: data.slice(0, 2).map(item => ({ 
        [sortConfig.field]: item[sortConfig.field],
        allKeys: Object.keys(item)
      }))
    });
    
    // Helper function to extract numeric value from formatted strings
    const extractNumericValue = (value) => {
      if (typeof value === 'number') return value;
      if (typeof value !== 'string') return 0;
      
      // Handle currency formats like "$11.2K", "$1.2M"
      if (value.includes('$')) {
        const cleanValue = value.replace(/[$,]/g, '');
        if (cleanValue.includes('K')) {
          return parseFloat(cleanValue.replace('K', '')) * 1000;
        } else if (cleanValue.includes('M')) {
          return parseFloat(cleanValue.replace('M', '')) * 1000000;
        } else {
          return parseFloat(cleanValue) || 0;
        }
      }
      
      // Handle percentage formats like "10.36%"
      if (value.includes('%')) {
        return parseFloat(value.replace('%', '')) || 0;
      }
      
      // Handle multiplier formats like "4.98x"
      if (value.includes('x')) {
        return parseFloat(value.replace('x', '')) || 0;
      }
      
      // Handle regular numbers
      return parseFloat(value) || 0;
    };
    
    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.field];
      let bValue = b[sortConfig.field];
      
      console.log(`🔍 Comparing field '${sortConfig.field}':`, { aValue, bValue, aType: typeof aValue, bType: typeof bValue });
      
      // Check if this is a text field (like ad names)
      const isTextField = sortConfig.field.toLowerCase().includes('name') || 
                         sortConfig.field.toLowerCase().includes('title') || 
                         sortConfig.field.toLowerCase().includes('ad_name');
      
      if (isTextField) {
        // String comparison for text fields
        aValue = (aValue || '').toLowerCase();
        bValue = (bValue || '').toLowerCase();
      } else {
        // Numeric comparison for all other fields
        aValue = extractNumericValue(aValue);
        bValue = extractNumericValue(bValue);
      }
      
      if (sortConfig.order === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  // Dynamic table helper functions
  const createDynamicTable = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return { columns: [], rows: [] };
    }
    
    try {
      // Get all unique keys from the data, ensuring data integrity
      const allKeys = new Set();
      data.forEach(item => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          Object.keys(item).forEach(key => {
            if (key && typeof key === 'string') {
              allKeys.add(key);
            }
          });
        }
      });
      
          // Convert to array and filter out common metadata fields
    const columns = Array.from(allKeys)
      .filter(key => ![
        'raw_ad_id', 'raw_campaign_id', 'raw_account_id', 'asset_id', 
        'raw_title', 'raw_title_', 'rawtitle', 'rawtitle_',
        'rawTitle', 'rawType', 'rawFeature', // camelCase versions
        'id', 'raw_id', 'account_id', 'campaign_id',
        // Additional technical fields to hide
        'creative_url', 'media_type', 'ad_format', 'purchases', 'purchase_value',
        'raw_asset_id', 'clip_sno', 'f_ad_type', 'video_ad_type'
      ].includes(key))
      .map(key => ({
        key: String(key), // Ensure key is a string
        label: String(key).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        type: getColumnType(data, key)
      }))
      .sort((a, b) => {
        // Priority order: ad_name first, then key metrics, then others
        if (a.key === 'ad_name') return -1;
        if (b.key === 'ad_name') return 1;
        if (a.key === 'ctr') return -1;
        if (b.key === 'ctr') return 1;
        if (a.key === 'roas') return -1;
        if (b.key === 'roas') return 1;
        if (a.key === 'spend') return -1;
        if (b.key === 'spend') return 1;
        if (a.key === 'impressions') return -1;
        if (b.key === 'impressions') return 1;
        return 0;
      });
      
      return { columns, rows: data };
    } catch (error) {
      console.error('Error creating dynamic table:', error, 'Data:', data);
      return { columns: [], rows: [] };
    }
  };
  
  // Determine column type for formatting
  const getColumnType = (data, key) => {
    try {
      if (!data || !Array.isArray(data) || !key) return 'text';
      
      const sampleValues = data.slice(0, 5)
        .map(item => item && typeof item === 'object' ? item[key] : null)
        .filter(v => v !== null && v !== undefined);
      
      if (sampleValues.length === 0) return 'text';
      
      const firstValue = sampleValues[0];
      if (typeof firstValue === 'number') {
        // Check if it's currency, percentage, or regular number
        if (key.toLowerCase().includes('spend') || key.toLowerCase().includes('cost') || key.toLowerCase().includes('amount') || key.toLowerCase().includes('revenue')) {
          return 'currency';
        } else if (key.toLowerCase().includes('ctr') || key.toLowerCase().includes('rate')) {
          return 'percentage';
        } else if (key.toLowerCase().includes('roas')) {
          return 'multiplier';
        } else {
          return 'number';
        }
      }
      return 'text';
    } catch (error) {
      console.error('Error determining column type:', error, 'Key:', key, 'Data:', data);
      return 'text';
    }
  };
  
  // Format value based on column type
  const formatValue = (value, type) => {
    try {
      if (value === null || value === undefined) return 'N/A';
      
      switch (type) {
        case 'currency':
          return typeof value === 'number' ? `$${value.toFixed(2)}` : String(value);
        case 'percentage':
          return typeof value === 'number' ? `${value.toFixed(2)}%` : String(value);
        case 'multiplier':
          return typeof value === 'number' ? `${value.toFixed(2)}x` : String(value);
        case 'number':
          return typeof value === 'number' ? value.toLocaleString() : String(value);
        default:
          return String(value);
      }
    } catch (error) {
      console.error('Error formatting value:', error, 'Value:', value, 'Type:', type);
      return String(value || 'N/A');
    }
  };

  const loadDashboardData = useCallback(async (days = selectedRange, customStartDate = null, customEndDate = null, forceAdNameFilter = null) => {
    console.log('🚀 loadDashboardData called with:', { days, customStartDate, customEndDate, forceAdNameFilter });
    console.log('🚀 Current adNameFilter state:', adNameFilter);
    console.log('🚀 Force filter parameter:', forceAdNameFilter);
    console.log('🚀 Data loaded status:', dataLoaded);
    
    // Use the forced filter if provided, otherwise use the state
    const effectiveAdNameFilter = forceAdNameFilter || adNameFilter;
    
    // Validate and clean the ad name filter to prevent hanging on empty filters
    // When filter is cleared, pass undefined (matches backend default behavior)
    const cleanAdNameFilter = effectiveAdNameFilter && 
      effectiveAdNameFilter.keywords && 
      Array.isArray(effectiveAdNameFilter.keywords) &&
      effectiveAdNameFilter.keywords.length > 0 && 
      effectiveAdNameFilter.keywords.some(keyword => keyword && typeof keyword === 'string' && keyword.trim().length > 0) 
      ? effectiveAdNameFilter 
      : undefined; // undefined = no filter = backend default behavior
    
    console.log('🚀 Effective ad name filter to use:', effectiveAdNameFilter);
    console.log('🚀 Cleaned ad name filter:', cleanAdNameFilter);
    console.log('🚀 Keywords validation:', {
      hasFilter: !!effectiveAdNameFilter,
      hasKeywords: !!(effectiveAdNameFilter && effectiveAdNameFilter.keywords),
      isArray: Array.isArray(effectiveAdNameFilter?.keywords),
      keywordsLength: effectiveAdNameFilter?.keywords?.length || 0,
      validKeywords: effectiveAdNameFilter?.keywords?.filter(keyword => keyword && typeof keyword === 'string' && keyword.trim().length > 0) || [],
      logic: effectiveAdNameFilter?.logic
    });
    
    // Prevent multiple simultaneous loads
    if (isLoading) {
      console.log('🚀 Dashboard already loading, skipping...');
      return;
    }
    
    setIsLoading(true);
    setDashboardLoadingProgress(0);
    
    // Get dynamic account ID
    const accountId = getAccountId();
    
    // Start progress bar for dashboard loading
    const progressInterval = startDashboardProgress(60000); // 60 seconds
    
    // Set all sections to loading
    setSectionLoading({
      metrics: true,
      trends: true,
      adTypes: true,
      highestSpendingFeatures: true,
      topContent: !isTopPerformingConfigured // Don't set loading if configured
    });

    try {
      // Calculate comparison periods
      const currentPeriod = days;
      
      // Handle custom date ranges vs preset ranges
      let currentStart, currentEnd, previousStart, previousEnd;
      
      if (customStartDate && customEndDate) {
        // Custom date range: use actual dates
        // Parse dates consistently by creating them at local midnight to avoid timezone issues
        const start = new Date(customStartDate + 'T00:00:00');
        const end = new Date(customEndDate + 'T00:00:00');
        const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
        
        // Format dates for SQL - preserve local date without timezone conversion
        const startDateStr = start.toLocaleDateString('en-CA'); // YYYY-MM-DD format
        const endDateStr = end.toLocaleDateString('en-CA'); // YYYY-MM-DD format
        
        // For custom date ranges, we need both start and end dates
        currentStart = `'${startDateStr}'`;
        currentEnd = `'${endDateStr}'`;
        
        // Calculate previous period (same duration before the custom start date)
        const previousStartDate = new Date(start.getTime() - (daysDiff * 24 * 60 * 60 * 1000)); // daysDiff days before start
        const previousEndDate = new Date(start.getTime() - (24 * 60 * 60 * 1000)); // Day before start
        previousStart = `'${previousStartDate.toLocaleDateString('en-CA')}'`;
        previousEnd = `'${previousEndDate.toLocaleDateString('en-CA')}'`;
        
        console.log('Custom date range variables:', { 
          customStartDate, 
          customEndDate, 
          currentStart, 
          currentEnd,
          previousStart, 
          previousEnd,
          daysDiff 
        });
      } else {
        // Preset range: use days from current date
        // Use timezone-aware CURRENT_DATE to match time series chart
        currentStart = `(CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '${currentPeriod} days'`;
        currentEnd = null; // Not needed for preset ranges
        previousStart = `(CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '${currentPeriod * 2} days'`;
        previousEnd = `(CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '${currentPeriod} days'`;
        
        console.log('Preset date range variables:', { currentPeriod, currentStart, previousStart, previousEnd });
      }

      // Determine trend grouping (daily vs weekly)
      const trendGrouping = days > 30 ? 'week' : 'day';
      const trendLimit = days > 30 ? Math.ceil(days / 7) : days;

      // Load metrics first for quick initial rendering, then other sections
      setDashboardLoadingProgress(15);
      
      // Use direct database service instead of AI query
      console.log('Fetching metrics with direct database service');
      console.log('Metrics filter details:', { 
        logic: adNameFilter.logic 
      });
      
      // Add timeout protection to prevent metrics from hanging
      const metricsResponse = await Promise.race([
        dashboardService.getMetrics(
          accountId, 
          currentStart, 
          currentEnd, 
          cleanAdNameFilter
        ),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Metrics query timeout')), 10000)
        )
      ]);
      
      // Process metrics immediately for quick display
      let currentMetrics = {};
      console.log('Metrics response:', metricsResponse);
      
      try {
        // Check if response is valid and has data
        if (metricsResponse && metricsResponse.success && metricsResponse.data && Array.isArray(metricsResponse.data) && metricsResponse.data.length > 0) {
          const row = metricsResponse.data[0];
          console.log('Raw metrics row:', row);
          // Dynamically extract all available metrics from the response
          Object.keys(row).forEach(key => {
            if (key === 'active_ads') return; // Skip non-metric fields
            const value = row[key];
            if (typeof value === 'number' || !isNaN(parseFloat(value))) {
              currentMetrics[key] = parseFloat(value) || 0;
            } else {
              currentMetrics[key] = value || 0;
            }
          });
          console.log('Processed currentMetrics:', currentMetrics);
        } else {
          console.log('No metrics data found in response, setting defaults');
          // Set default metrics when no data is returned
          currentMetrics = {
            total_impressions: 0,
            total_spend: 0,
            total_clicks: 0,
            total_conversions: 0,
            total_revenue: 0
          };
        }
      } catch (error) {
        console.error('Error processing metrics response:', error);
        // Set default metrics to prevent loading state from getting stuck
        currentMetrics = {
          total_impressions: 0,
          total_spend: 0,
          total_clicks: 0,
          total_conversions: 0,
          total_revenue: 0
        };
      }
      
      // No fallback needed - just use the data we have (even if empty)
      console.log('Using current metrics data (no fallback)');
      
      // Create dynamic metrics object
      const metricsObject = {};
      console.log('Processing metrics - currentMetrics keys:', Object.keys(currentMetrics));
      
      // Always ensure we have default metrics to prevent loading state from getting stuck
      if (Object.keys(currentMetrics).length === 0) {
        console.log('No metrics data available, setting defaults');
        currentMetrics = {
          total_impressions: 0,
          total_spend: 0,
          total_clicks: 0,
          total_conversions: 0,
          total_revenue: 0
        };
      }
      Object.keys(currentMetrics).forEach(key => {
        const value = currentMetrics[key];
        // Convert snake_case to camelCase with proper acronym handling
        let camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
        
        // Handle acronyms properly for frontend compatibility
        if (camelKey === 'avgCtr') camelKey = 'avgCTR';
        if (camelKey === 'avgCpc') camelKey = 'avgCPC';
        if (camelKey === 'avgRoas') camelKey = 'avgROAS';
        if (camelKey === 'avgCpa') camelKey = 'avgCPA';
        if (camelKey === 'avgCvr') camelKey = 'avgCVR';
        
        console.log(`Processing metric: ${key} -> ${camelKey}, value: ${value}, type: ${typeof value}`);
        
        if (key.includes('total_')) {
          if (key === 'total_spend' || key === 'total_revenue') {
            metricsObject[camelKey] = formatCurrency(value);
          } else {
            metricsObject[camelKey] = formatNumber(value);
          }
        } else if (key.includes('avg_') && (key.includes('ctr') || key.includes('cvr'))) {
          console.log(`Processing CTR/CVR metric: ${key}, value: ${value}`);
          metricsObject[camelKey] = value === 0 ? '0%' : `${value}%`;
        } else if (key.includes('roas')) {
          metricsObject[camelKey] = value === 0 ? '0.00' : value.toFixed(2);
        } else if (key.includes('cpc') || key.includes('cpa')) {
          metricsObject[camelKey] = formatCurrency(value);
        } else {
          metricsObject[camelKey] = formatNumber(value);
        }
        // Add growth placeholder (will be updated later)
        let growthKey;
        if (key.includes('total_')) {
          growthKey = camelKey.replace('total', '') + 'Growth';
        } else if (key.includes('avg_')) {
          growthKey = camelKey + 'Growth';
        } else {
          growthKey = camelKey + 'Growth';
        }
        metricsObject[growthKey] = '+0%';
      });
      
      console.log('Final metricsObject:', metricsObject);
      
      setDashboardData(prev => ({
        ...prev,
        metrics: metricsObject
      }));
      
      // Always ensure metrics loading state is reset
      setSectionLoading(prev => ({ ...prev, metrics: false }));
      setDashboardLoadingProgress(40);

      // Load remaining sections in parallel
      setDashboardLoadingProgress(30);
      console.log('Starting parallel API calls...');
      // Fetch all data using direct database service (no AI processing)
      console.log('Starting direct database queries...');
      
      const previousMetricsResponse = await dashboardService.getPreviousMetrics(accountId, previousStart, previousEnd);
      const trendsResponse = await dashboardService.getTrends(accountId, currentStart, currentEnd, trendGrouping, trendLimit, cleanAdNameFilter);
      const topContentResponse = await dashboardService.getTopContent(accountId, currentStart, currentEnd, cleanAdNameFilter);
            const adTypesResponse = await dashboardService.getAdTypes(accountId, currentStart, currentEnd, cleanAdNameFilter);
      const fastGrowingFeaturesResponse = await dashboardService.getFeatures(accountId, currentStart, currentEnd, cleanAdNameFilter);
      console.log('All direct database queries completed successfully');

      // clearInterval(progressInterval); // Removed progress interval clear
      // setLoadingProgress(100); // Removed setLoadingProgress

      // Calculate period-over-period changes
      const calculateGrowth = (current, previous) => {
        if (!previous || previous === 0) return '+0%';
        const change = ((current - previous) / previous) * 100;
        const result = `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
        console.log(`calculateGrowth(${current}, ${previous}) = ${result}`);
        return result;
      };

      // Process previous metrics data and update growth calculations
      let previousMetrics = {};
      if (previousMetricsResponse.success && 
          previousMetricsResponse.data && 
          previousMetricsResponse.data.length > 0) {
        const row = previousMetricsResponse.data[0];
        console.log('Previous metrics raw data:', row);
        Object.keys(row).forEach(key => {
          const value = row[key];
          if (typeof value === 'number' || !isNaN(parseFloat(value))) {
            previousMetrics[key] = parseFloat(value) || 0;
          } else {
            previousMetrics[key] = value || 0;
          }
        });
        console.log('Processed previous metrics:', previousMetrics);
      }

      // Update metrics with growth calculations
      const updatedMetrics = { ...metricsObject }; // Use metricsObject instead of dashboardData.metrics
      console.log('Current metrics for growth calculation:', currentMetrics);
      
      // Calculate growth for basic metrics that we have from previous period
      const basicMetrics = ['total_impressions', 'total_spend', 'total_clicks', 'total_conversions', 'total_revenue'];
      basicMetrics.forEach(key => {
        if (currentMetrics[key] !== undefined) {
          const currentValue = currentMetrics[key];
          const previousValue = previousMetrics[key] || 0;
          // Convert snake_case to camelCase with proper acronym handling
          let camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
          
          // Handle acronyms properly for frontend compatibility
          if (camelKey === 'avgCtr') camelKey = 'avgCTR';
          if (camelKey === 'avgCpc') camelKey = 'avgCPC';
          if (camelKey === 'avgRoas') camelKey = 'avgROAS';
          if (camelKey === 'avgCpa') camelKey = 'avgCPA';
          if (camelKey === 'avgCvr') camelKey = 'avgCVR';
          
          const growthKey = camelKey.replace('total', '').replace('avg', '') + 'Growth';
          updatedMetrics[growthKey] = calculateGrowth(currentValue, previousValue);
        }
      });
      
      // For calculated metrics (avg_ctr, avg_cpc, avg_roas, avg_cpa, avg_cvr, video_completions), 
      // we need to calculate them from the basic metrics
      if (currentMetrics.total_impressions && currentMetrics.total_clicks && previousMetrics.total_impressions && previousMetrics.total_clicks) {
        const currentCTR = (currentMetrics.total_clicks / currentMetrics.total_impressions) * 100;
        const previousCTR = (previousMetrics.total_clicks / previousMetrics.total_impressions) * 100;
        console.log('CTR Growth calculation:', { currentCTR, previousCTR, currentClicks: currentMetrics.total_clicks, currentImpressions: currentMetrics.total_impressions, previousClicks: previousMetrics.total_clicks, previousImpressions: previousMetrics.total_impressions });
        updatedMetrics.avgCTRGrowth = calculateGrowth(currentCTR, previousCTR);
      }
      
      if (currentMetrics.total_spend && currentMetrics.total_clicks && previousMetrics.total_spend && previousMetrics.total_clicks) {
        const currentCPC = currentMetrics.total_spend / currentMetrics.total_clicks;
        const previousCPC = previousMetrics.total_spend / previousMetrics.total_clicks;
        console.log('CPC Growth calculation:', { currentCPC, previousCPC, currentSpend: currentMetrics.total_spend, currentClicks: currentMetrics.total_clicks, previousSpend: previousMetrics.total_spend, previousClicks: previousMetrics.total_clicks });
        updatedMetrics.avgCPCGrowth = calculateGrowth(currentCPC, previousCPC);
      }
      
      if (currentMetrics.total_revenue && currentMetrics.total_spend && previousMetrics.total_revenue && previousMetrics.total_spend) {
        const currentROAS = currentMetrics.total_revenue / currentMetrics.total_spend;
        const previousROAS = previousMetrics.total_revenue / previousMetrics.total_spend;
        console.log('ROAS Growth calculation:', { currentROAS, previousROAS, currentRevenue: currentMetrics.total_revenue, currentSpend: currentMetrics.total_spend, previousRevenue: previousMetrics.total_revenue, previousSpend: previousMetrics.total_spend });
        updatedMetrics.avgROASGrowth = calculateGrowth(currentROAS, previousROAS);
      }
      
      if (currentMetrics.total_spend && currentMetrics.total_conversions && previousMetrics.total_spend && previousMetrics.total_conversions) {
        const currentCPA = currentMetrics.total_spend / currentMetrics.total_conversions;
        const previousCPA = previousMetrics.total_spend / previousMetrics.total_conversions;
        console.log('CPA Growth calculation:', { currentCPA, previousCPA, currentSpend: currentMetrics.total_spend, currentConversions: currentMetrics.total_conversions, previousSpend: previousMetrics.total_spend, previousConversions: previousMetrics.total_conversions });
        updatedMetrics.avgCPAGrowth = calculateGrowth(currentCPA, previousCPA);
      }
      
      if (currentMetrics.total_clicks && currentMetrics.total_conversions && previousMetrics.total_clicks && previousMetrics.total_conversions) {
        const currentCVR = (currentMetrics.total_conversions / currentMetrics.total_clicks) * 100;
        const previousCVR = (previousMetrics.total_conversions / previousMetrics.total_clicks) * 100;
        console.log('CVR Growth calculation:', { currentCVR, previousCVR, currentConversions: currentMetrics.total_conversions, currentClicks: currentMetrics.total_clicks, previousConversions: previousMetrics.total_conversions, previousClicks: previousMetrics.total_clicks });
        updatedMetrics.avgCVRGrowth = calculateGrowth(currentCVR, previousCVR);
      }
      
      console.log('Final updatedMetrics with growth values:', updatedMetrics);

      setDashboardData(prev => ({
        ...prev,
        metrics: updatedMetrics
      }));





      // Process trends data - Modified for ROAS and spend only
      if (trendsResponse.success && 
          trendsResponse.data && 
          trendsResponse.data.length > 0) {
        console.log('Raw trends data:', trendsResponse.data);
        const performanceData = trendsResponse.data.map(row => {
          let processedName;
          if (trendGrouping === 'week') {
            processedName = `Week ${getWeekNumber(row.period)}`;
          } else {
            // Handle PostgreSQL timestamp format properly
            // row.period comes from DATE_TRUNC and might be in format like "2025-08-02T00:00:00.000Z"
            let dateObj;
            if (typeof row.period === 'string') {
              // If it's a string, try to parse it
              dateObj = new Date(row.period);
            } else {
              // If it's already a Date object, use it directly
              dateObj = row.period;
            }
            
            // Check if the date is valid
            if (isNaN(dateObj.getTime())) {
              console.error('Invalid date for period:', row.period);
              processedName = 'Invalid Date';
            } else {
              processedName = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            }
          }
          
          console.log('Processing trend row:', { 
            period: row.period, 
            periodType: typeof row.period,
            processedName,
            dateObj: trendGrouping === 'week' ? 'N/A' : new Date(row.period),
            weekNumber: trendGrouping === 'week' ? getWeekNumber(row.period) : 'N/A'
          });
          
          return {
            name: processedName,
            spend: parseFloat(row.total_spend) || 0,
            roas: parseFloat(row.roas) || 0
          };
        }).reverse(); // Reverse to show chronological order
        console.log('Final performance data:', performanceData);
        setDashboardData(prev => ({ ...prev, performanceData }));
      } else {
        // Clear trends data when query returns no results
        console.log('No trends data returned, clearing performance data');
        setDashboardData(prev => ({ ...prev, performanceData: [] }));
      }
      setSectionLoading(prev => ({ ...prev, trends: false }));
      setDashboardLoadingProgress(50);

      // Process top content data with real-time growth calculation
      // Skip processing if top content is configured by user
      if (!isTopPerformingConfigured && topContentResponse.success && 
          topContentResponse.data && 
          topContentResponse.data.length > 0) {
        // Get previous period data for growth calculation using direct database service
        const previousTopContentResponse = await dashboardService.getTopContent(accountId, previousStart, previousEnd);

        // Create map of previous period data
        const previousTopContentMap = {};
        if (previousTopContentResponse.success && previousTopContentResponse.data) {
          previousTopContentResponse.data.forEach(row => {
            previousTopContentMap[row.ad_name] = {
              impressions: parseInt(row.total_impressions) || 0,
              ctr: parseFloat(row.ctr) || 0
            };
          });
        }

        const topContent = topContentResponse.data.map(row => {
          const currentImpressions = parseInt(row.total_impressions) || 0;
          const previousData = previousTopContentMap[row.ad_name] || { impressions: 0, ctr: 0 };
          const growthRate = previousData.impressions > 0 ? ((currentImpressions - previousData.impressions) / previousData.impressions) * 100 : 0;
          
          return {
            title: row.ad_name || 'Unknown Ad',
            views: formatNumber(currentImpressions),
            spend: formatCurrency(parseFloat(row.total_spend) || 0),
            engagement: row.ctr ? `${parseFloat(row.ctr).toFixed(1)}%` : '0%',
            roas: row.roas ? `${parseFloat(row.roas).toFixed(2)}x` : '0.00x',
            rawTitle: row.ad_name // Keep original for query generation
          };
        });
        setDashboardData(prev => ({ ...prev, topContent }));
      } else if (!isTopPerformingConfigured) {
        // Clear top content data when query returns no results (only if not configured)
        console.log('No top content data returned, clearing top content');
        setDashboardData(prev => ({ ...prev, topContent: [] }));
      }
      
      // Only update loading state if we're not configured
      if (!isTopPerformingConfigured) {
        setSectionLoading(prev => ({ ...prev, topContent: false }));
      }
      setDashboardLoadingProgress(70);

      // Process ad types data with enhanced metrics
      if (adTypesResponse.success && 
          adTypesResponse.data && 
          adTypesResponse.data.length > 0) {
        const adTypes = adTypesResponse.data
          .filter(row => row.ad_type && row.ad_type !== 'Unknown' && !row.ad_type.includes('n/a') && !row.ad_type.includes('N/A'))
          .map(row => ({
            type: row.ad_type || 'Unknown',
            count: parseInt(row.count) || 0,
            impressions: formatNumber(parseInt(row.total_impressions) || 0),
            spend: formatCurrency(parseFloat(row.total_spend) || 0),
            ctr: row.ctr ? `${parseFloat(row.ctr).toFixed(1)}%` : '0%',
            roas: row.roas ? `${parseFloat(row.roas).toFixed(2)}x` : '0.00x',
            color: row.ad_type === 'VIDEO' ? '#8b5cf6' : 
                   row.ad_type === 'IMAGE' ? '#06b6d4' : 
                   row.ad_type === 'CAROUSEL' ? '#f59e0b' : '#10b981',
            rawType: row.ad_type // Keep original for query generation
          }));
        setDashboardData(prev => ({ ...prev, adTypes }));
      } else {
        // Clear ad types data when query returns no results
        console.log('No ad types data returned, clearing ad types');
        setDashboardData(prev => ({ ...prev, adTypes: [] }));
      }
      setSectionLoading(prev => ({ ...prev, adTypes: false }));
      setDashboardLoadingProgress(80);

             // Process highest spending features data - New unnest structure
       if (fastGrowingFeaturesResponse.success && 
           fastGrowingFeaturesResponse.data && 
           fastGrowingFeaturesResponse.data.length > 0) {
         console.log('Raw features data:', fastGrowingFeaturesResponse.data);
         const fastGrowingFeatures = fastGrowingFeaturesResponse.data
           .filter(row => row.feature_value && row.feature_value !== 'Unknown' && !row.feature_value.includes('n/a') && !row.feature_value.includes('N/A') && !row.feature_value.includes('not applicable'))
           .map(row => {
             const spend = parseFloat(row.total_spend) || 0;
             
             return {
               feature: `${row.feature_name}: ${row.feature_value}`, // Format as "Category: Value"
               count: parseInt(row.number_of_ads) || 0,
               impressions: formatNumber(parseInt(row.total_impressions) || 0),
               spend: formatCurrency(spend),
               ctr: row.ctr ? `${parseFloat(row.ctr).toFixed(1)}%` : '0%',
               roas: row.roas ? `${parseFloat(row.roas).toFixed(2)}x` : '0.00x',
               rawFeature: row.feature_value, // Keep just the value for query generation
               spendValue: spend
             };
           })
           .sort((a, b) => b.spendValue - a.spendValue)
           .slice(0, 10);
         
         console.log('Processed features data:', fastGrowingFeatures);

                 setDashboardData(prev => ({ ...prev, fastGrowingFeatures }));
       } else {
         // Clear features data when query returns no results
         console.log('No features data returned, clearing fast growing features');
         setDashboardData(prev => ({ ...prev, fastGrowingFeatures: [] }));
       }
       setSectionLoading(prev => ({ ...prev, highestSpendingFeatures: false }));
      setDashboardLoadingProgress(90);

    } catch (error) {
      console.log('Dashboard data loading failed, using fallback data:', error);
      console.log('Error details:', error.message);
      console.log('Error stack:', error.stack);
      
      // Don't set fallback data - let empty results show empty tables/charts
      // Only preserve metrics that were already set correctly
      setDashboardData(prev => ({
        ...prev, // This preserves the metrics that were set earlier
        // Clear other data to show empty tables/charts when filters return no results
        performanceData: [],
        topContent: [],
        adTypes: [],
        fastGrowingFeatures: []
      }));
    } finally {
      clearInterval(progressInterval);
      completeDashboardProgress();
      setIsLoading(false);
    }
  }, [selectedRange, dashboardConfig, adNameFilter]); // Added dashboardConfig and adNameFilter dependencies

  // Load dashboard data without affecting configured top content
  const loadDashboardDataWithoutTopContent = useCallback(async (days = selectedRange, customStartDate = null, customEndDate = null, forceAdNameFilter = null) => {
    console.log('🚀 loadDashboardDataWithoutTopContent called - preserving configured top content');
    
    // Prevent multiple simultaneous loads
    if (isLoading) {
      console.log('🚀 Dashboard already loading, skipping...');
      return;
    }
    
    setIsLoading(true);
    setDashboardLoadingProgress(0);
    
    // Get dynamic account ID
    const accountId = getAccountId();
    
    // Start progress bar for dashboard loading
    const progressInterval = startDashboardProgress(60000); // 60 seconds
    
    // Set all sections to loading EXCEPT top content (preserve it)
    setSectionLoading({
      metrics: true,
      trends: true,
      adTypes: true,
      highestSpendingFeatures: true,
      topContent: false // Don't set loading for configured top content
    });

    try {
      // Calculate comparison periods
      const currentPeriod = days;
      
      // Handle custom date ranges vs preset ranges
      let currentStart, currentEnd, previousStart, previousEnd;
      
      if (customStartDate && customEndDate) {
        // Custom date range: use actual dates
        const start = new Date(customStartDate + 'T00:00:00');
        const end = new Date(customEndDate + 'T00:00:00');
        const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
        
        // Format dates for SQL - preserve local date without timezone conversion
        const startDateStr = start.toLocaleDateString('en-CA'); // YYYY-MM-DD format
        const endDateStr = end.toLocaleDateString('en-CA'); // YYYY-MM-DD format
        
        currentStart = `'${startDateStr}'`;
        currentEnd = `'${endDateStr}'`;
        
        // Calculate previous period (same duration before the custom start date)
        const previousStartDate = new Date(start.getTime() - (daysDiff * 24 * 60 * 60 * 1000));
        const previousEndDate = new Date(start.getTime() - (24 * 60 * 60 * 1000));
        previousStart = `'${previousStartDate.toLocaleDateString('en-CA')}'`;
        previousEnd = `'${previousEndDate.toLocaleDateString('en-CA')}'`;
      } else {
        // Preset range: use days from current date
        // Use timezone-aware CURRENT_DATE to match time series chart
        currentStart = `(CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '${currentPeriod} days'`;
        currentEnd = null;
        previousStart = `(CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '${currentPeriod * 2} days'`;
        previousEnd = `(CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '${currentPeriod} days'`;
      }

      // Determine trend grouping (daily vs weekly)
      const trendGrouping = days > 30 ? 'week' : 'day';
      const trendLimit = days > 30 ? Math.ceil(days / 7) : days;

      // Helper function to build date filter for SQL queries
      const buildDateFilter = (startDate, endDate = null) => {
        if (endDate) {
          return `date >= ${startDate} AND date <= ${endDate}`;
        } else {
          return `date >= ${startDate}`;
        }
      };

      // Load metrics first for quick initial rendering
      setDashboardLoadingProgress(15);
      
      // Use the forced filter if provided, otherwise use the state
      const effectiveAdNameFilter = forceAdNameFilter || adNameFilter;
      
      // Validate and clean the ad name filter to prevent hanging on empty filters
      // When filter is cleared, pass undefined (matches backend default behavior)
      const cleanAdNameFilter = effectiveAdNameFilter && 
        effectiveAdNameFilter.keywords && 
        Array.isArray(effectiveAdNameFilter.keywords) &&
        effectiveAdNameFilter.keywords.length > 0 && 
        effectiveAdNameFilter.keywords.some(keyword => keyword && typeof keyword === 'string' && keyword.trim().length > 0) 
        ? effectiveAdNameFilter 
        : undefined; // undefined = no filter = backend default behavior
      
      console.log('Ad name filter validation:', { 
        original: effectiveAdNameFilter, 
        cleaned: cleanAdNameFilter,
        hasValidKeywords: cleanAdNameFilter !== undefined
      });
      console.log('Keywords validation details:', {
        hasFilter: !!effectiveAdNameFilter,
        hasKeywords: !!(effectiveAdNameFilter && effectiveAdNameFilter.keywords),
        isArray: Array.isArray(effectiveAdNameFilter?.keywords),
        keywordsLength: effectiveAdNameFilter?.keywords?.length || 0,
        validKeywords: effectiveAdNameFilter?.keywords?.filter(keyword => keyword && typeof keyword === 'string' && keyword.trim().length > 0) || [],
        logic: effectiveAdNameFilter?.logic
      });
      
      // Use direct database service instead of AI query
      console.log('Fetching metrics with direct database service (without top content)');
      const metricsResponse = await dashboardService.getMetrics(
        accountId, 
        currentStart, 
        currentEnd, 
        cleanAdNameFilter
      );
      
      // Process metrics immediately for quick display
      let currentMetrics = {};
      
      try {
        if (metricsResponse.success && metricsResponse.data && metricsResponse.data.length > 0) {
          const row = metricsResponse.data[0];
          Object.keys(row).forEach(key => {
            if (key === 'active_ads') return;
            const value = row[key];
            if (typeof value === 'number' || !isNaN(parseFloat(value))) {
              currentMetrics[key] = parseFloat(value) || 0;
            } else {
              currentMetrics[key] = value || 0;
            }
          });
        }
      } catch (error) {
        console.error('Error processing metrics response:', error);
        // Set default metrics to prevent loading state from getting stuck
        currentMetrics = {
          total_impressions: 0,
          total_spend: 0,
          total_clicks: 0,
          total_conversions: 0,
          total_revenue: 0
        };
      }
      
      // No fallback needed - just use the data we have (even if empty)
      console.log('Using current metrics data (no fallback)');
      
      // Always ensure we have default metrics to prevent loading state from getting stuck
      if (Object.keys(currentMetrics).length === 0) {
        console.log('No metrics data available, setting defaults');
        currentMetrics = {
          total_impressions: 0,
          total_spend: 0,
          total_clicks: 0,
          total_conversions: 0,
          total_revenue: 0
        };
      }
      
      setDashboardLoadingProgress(30);
      
      // Load remaining sections using direct database service (EXCEPT top content)
      console.log('Starting direct database queries (without top content)...');
      
      const previousMetricsResponse = await dashboardService.getPreviousMetrics(accountId, previousStart, previousEnd);
      const trendsResponse = await dashboardService.getTrends(accountId, currentStart, currentEnd, trendGrouping, trendLimit, cleanAdNameFilter);
      const adTypesResponse = await dashboardService.getAdTypes(accountId, currentStart, currentEnd, cleanAdNameFilter);
      const fastGrowingFeaturesResponse = await dashboardService.getFeatures(accountId, currentStart, currentEnd, cleanAdNameFilter);

      // Process previous metrics data
      let previousMetrics = {};
      try {
        if (previousMetricsResponse.success && previousMetricsResponse.data && previousMetricsResponse.data.length > 0) {
          const row = previousMetricsResponse.data[0];
          Object.keys(row).forEach(key => {
            const value = row[key];
            if (typeof value === 'number' || !isNaN(parseFloat(value))) {
              previousMetrics[key] = parseFloat(value) || 0;
            } else {
              previousMetrics[key] = value || 0;
            }
          });
        }
      } catch (error) {
        console.error('Error processing previous metrics response:', error);
        previousMetrics = {
          total_impressions: 0,
          total_spend: 0,
          total_clicks: 0,
          total_conversions: 0,
          total_revenue: 0
        };
      }
      
      // Process trends data
      if (trendsResponse.success && trendsResponse.data && trendsResponse.data.length > 0) {
        const performanceData = trendsResponse.data.map(row => {
          let processedName;
          if (trendGrouping === 'week') {
            processedName = `Week ${getWeekNumber(row.period)}`;
          } else {
            let dateObj;
            if (typeof row.period === 'string') {
              dateObj = new Date(row.period);
            } else {
              dateObj = row.period;
            }
            
            if (isNaN(dateObj.getTime())) {
              processedName = 'Invalid Date';
            } else {
              processedName = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            }
          }
          
          return {
            name: processedName,
            spend: parseFloat(row.total_spend) || 0,
            roas: parseFloat(row.roas) || 0
          };
        }).reverse();
        
        setDashboardData(prev => ({ ...prev, performanceData }));
      } else {
        // Clear trends data when query returns no results
        console.log('No trends data returned, clearing performance data');
        setDashboardData(prev => ({ ...prev, performanceData: [] }));
      }
      setSectionLoading(prev => ({ ...prev, trends: false }));
      setDashboardLoadingProgress(50);
      
      // Process ad types data
      if (adTypesResponse.success && adTypesResponse.data && adTypesResponse.data.length > 0) {
        const adTypes = adTypesResponse.data
          .filter(row => row.ad_type && row.ad_type !== 'Unknown' && !row.ad_type.includes('n/a') && !row.ad_type.includes('N/A'))
          .map(row => ({
            type: row.ad_type || 'Unknown',
            count: parseInt(row.count) || 0,
            impressions: formatNumber(parseInt(row.total_impressions) || 0),
            spend: formatCurrency(parseFloat(row.total_spend) || 0),
            ctr: row.ctr ? `${parseFloat(row.ctr).toFixed(1)}%` : '0%',
            roas: row.roas ? `${parseFloat(row.roas).toFixed(2)}x` : '0.00x',
            color: row.ad_type === 'VIDEO' ? '#8b5cf6' : 
                   row.ad_type === 'IMAGE' ? '#06b6d4' : 
                   row.ad_type === 'CAROUSEL' ? '#f59e0b' : '#10b981',
            rawType: row.ad_type
          }));
        setDashboardData(prev => ({ ...prev, adTypes }));
      } else {
        // Clear ad types data when query returns no results
        console.log('No ad types data returned, clearing ad types');
        setDashboardData(prev => ({ ...prev, adTypes: [] }));
      }
      setSectionLoading(prev => ({ ...prev, adTypes: false }));
      setDashboardLoadingProgress(80);
      
      // Process highest spending features data
      if (fastGrowingFeaturesResponse.success && fastGrowingFeaturesResponse.data && fastGrowingFeaturesResponse.data.length > 0) {
        const fastGrowingFeatures = fastGrowingFeaturesResponse.data
          .filter(row => row.feature_value && row.feature_value !== 'Unknown' && !row.feature_value.includes('n/a') && !row.feature_value.includes('N/A') && !row.feature_value.includes('not applicable'))
          .map(row => {
            const spend = parseFloat(row.total_spend) || 0;
            
            return {
              feature: `${row.feature_name}: ${row.feature_value}`,
              count: parseInt(row.number_of_ads) || 0,
              impressions: formatNumber(parseInt(row.total_impressions) || 0),
              spend: formatCurrency(spend),
              ctr: row.ctr ? `${parseFloat(row.ctr).toFixed(1)}%` : '0%',
              roas: row.roas ? `${parseFloat(row.roas).toFixed(2)}x` : '0.00x',
              rawFeature: row.feature_value,
              spendValue: spend
            };
          })
          .sort((a, b) => b.spendValue - a.spendValue)
          .slice(0, 10);
        
        setDashboardData(prev => ({ ...prev, fastGrowingFeatures }));
      } else {
        // Clear features data when query returns no results
        console.log('No features data returned, clearing fast growing features');
        setDashboardData(prev => ({ ...prev, fastGrowingFeatures: [] }));
      }
      setSectionLoading(prev => ({ ...prev, highestSpendingFeatures: false }));
      setDashboardLoadingProgress(90);
      
      // Calculate metric comparisons and update dashboard data
      const metricsObject = {};
      Object.keys(currentMetrics).forEach(key => {
        const currentValue = currentMetrics[key] || 0;
        const previousValue = previousMetrics[key] || 0;
        
        if (key.includes('total_')) {
          const cleanKey = key.replace('total_', '');
          if (cleanKey === 'impressions') {
            metricsObject[`total${cleanKey.charAt(0).toUpperCase() + cleanKey.slice(1)}`] = formatNumber(currentValue);
          } else if (cleanKey === 'spend') {
            metricsObject[`total${cleanKey.charAt(0).toUpperCase() + cleanKey.slice(1)}`] = formatCurrency(currentValue);
          } else {
            metricsObject[`total${cleanKey.charAt(0).toUpperCase() + cleanKey.slice(1)}`] = formatNumber(currentValue);
          }
        } else if (key.includes('avg_')) {
          const cleanKey = key.replace('avg_', '');
          if (cleanKey === 'ctr' || cleanKey === 'cvr') {
            metricsObject[`avg${cleanKey.toUpperCase()}`] = `${currentValue.toFixed(1)}%`;
          } else if (cleanKey === 'roas') {
            metricsObject[`avg${cleanKey.toUpperCase()}`] = `${currentValue.toFixed(2)}x`;
          } else {
            metricsObject[`avg${cleanKey.toUpperCase()}`] = formatCurrency(currentValue);
          }
        }
      });
      
      // Update metrics in dashboard data
      setDashboardData(prev => ({
        ...prev,
        metrics: {
          ...prev.metrics,
          ...metricsObject
        }
      }));
      
      // Final safety check: ensure all loading states are reset on success
      setSectionLoading(prev => ({
        ...prev,
        metrics: false,
        trends: false,
        topContent: false,
        adTypes: false,
        highestSpendingFeatures: false
      }));
      
    } catch (error) {
      console.log('Dashboard data loading failed, using fallback data:', error);
      console.log('Error details:', error.message);
      console.log('Error stack:', error.stack);
      
      // Don't set fallback data - let empty results show empty tables/charts
      // Only preserve the top content that was already configured
      setDashboardData(prev => ({
        ...prev, // This preserves the top content
        // Clear other data to show empty tables/charts when filters return no results
        performanceData: [],
        adTypes: [],
        fastGrowingFeatures: []
      }));
      
      // Final safety check: ensure all loading states are reset
      setSectionLoading(prev => ({
        ...prev,
        metrics: false,
        trends: false,
        topContent: false,
        adTypes: false,
        highestSpendingFeatures: false
      }));
    } finally {
      clearInterval(progressInterval);
      completeDashboardProgress();
      setIsLoading(false);
    }
  }, [selectedRange, dashboardConfig, adNameFilter]);

  // Load initial dashboard data when authenticated
  useEffect(() => {
    // Wait for auto-load attempt to complete before loading default data
    if (isAuthenticated && !dataLoaded && hasAutoLoadedReport) {
      console.log('📊 No report auto-loaded, loading default dashboard data');
      // Open chat in large size during initial loading to keep users engaged
      setChatSize('small');
      loadDashboardData();
      setDataLoaded(true);
    }
  }, [isAuthenticated, dataLoaded, hasAutoLoadedReport]);

  const handleDateRangeChange = (days) => {
    setSelectedRange(days);
    setDateRangeType('preset'); // Set to preset mode when using preset buttons
    setStartDate(''); // Clear custom date range
    setEndDate(''); // Clear custom date range
    setDataLoaded(false); // Reset to allow new data loading
    setRefreshTrigger(prev => prev + 1); // Force refresh
    
    // If top performing section is configured, preserve its data
    if (isTopPerformingConfigured) {
      console.log('📅 Date changed but top performing section is configured, preserving data');
      // Load other sections but preserve top content
      loadDashboardDataWithoutTopContent(days);
    } else {
      loadDashboardData(days);
    }
  };

  const handleCustomDaysSubmit = () => {
    if (!customDays || customDays === '') {
      toast.error('Please enter a number of days');
      return;
    }
    if (customDays >= 1 && customDays <= 365) {
      setSelectedRange(customDays);
      setDateRangeType('preset'); // Set to preset mode when using days input
      setStartDate(''); // Clear custom date range
      setEndDate(''); // Clear custom date range
      setDataLoaded(false); // Reset to allow new data loading
      setRefreshTrigger(prev => prev + 1); // Force refresh
      
      // If top performing section is configured, preserve its data
      if (isTopPerformingConfigured) {
        console.log('📅 Custom days changed but top performing section is configured, preserving data');
        loadDashboardDataWithoutTopContent(customDays);
      } else {
        loadDashboardData(customDays);
      }
      setShowDatePicker(false);
    } else {
      toast.error('Please enter a valid number between 1 and 365 days');
    }
  };

  const handleCustomDateRangeSubmit = () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    // Parse dates consistently by creating them at local midnight to avoid timezone issues
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    const today = new Date();
    
    if (start > end) {
      toast.error('Start date must be before end date');
      return;
    }
    
    if (end > today) {
      toast.error('End date cannot be in the future');
      return;
    }
    
    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    if (daysDiff > 365) {
      toast.error('Date range cannot exceed 365 days');
      return;
    }
    
    setSelectedRange(daysDiff);
    setDateRangeType('custom');
    setDataLoaded(false);
    
    // If top performing section is configured, preserve its data
    if (isTopPerformingConfigured) {
      console.log('📅 Custom date range changed but top performing section is configured, preserving data');
      loadDashboardDataWithoutTopContent(daysDiff, startDate, endDate);
    } else {
      // Pass the actual custom dates to loadDashboardData
      loadDashboardData(daysDiff, startDate, endDate);
    }
    setShowDatePicker(false);
  };

  const handlePresetRange = (days) => {
    setSelectedRange(days);
    setDateRangeType('preset');
    setDataLoaded(false);
    
    // If top performing section is configured, preserve its data
    if (isTopPerformingConfigured) {
      console.log('📅 Preset range changed but top performing section is configured, preserving data');
      loadDashboardDataWithoutTopContent(days);
    } else {
      loadDashboardData(days);
    }
    setShowDatePicker(false);
  };

  // Handle pending ad name filter changes (not applied until confirmed)
  const handlePendingAdNameFilterChange = (newFilter) => {
    setPendingAdNameFilter(newFilter);
  };

      // Apply the pending ad name filter changes
    const applyAdNameFilter = () => {
      
      // Close the modal after applying
      setShowDatePicker(false);
      
      // Show loading state for filter application
      setSectionLoading(prev => ({ 
        ...prev, 
        topContent: !isTopPerformingConfigured, // Don't set loading if configured
        trends: true, 
        adTypes: true, 
        highestSpendingFeatures: true 
      }));
      
      // Update the filter state immediately
      setAdNameFilter(pendingAdNameFilter);
      
      // Force refresh all dashboards
      setRefreshTrigger(prev => prev + 1);
      console.log('🔄 Force refresh triggered (filter applied)');
      
      // Preserve top content data if section is configured
      if (isTopPerformingConfigured) {
        console.log('🎯 Top performing section is configured, preserving data during filter change');
      }
      
      // Reset the custom content flag so ad name filter can work again
      // But preserve it if top performing section is configured
      if (!isTopPerformingConfigured) {
        setDashboardData(prev => ({
          ...prev,
          isCustomTopContent: false
        }));
      }
      
      console.log('🎯 Modal closed, filter state updated, calling reloadDashboardWithFilter...');
      
      // Directly reload the dashboard with the new filter
      // This bypasses the dependency issues in loadDashboardData
      reloadDashboardWithFilter(pendingAdNameFilter);
    };

    // Reload function that refreshes ALL dashboard sections with new filter
    const reloadDashboardWithFilter = (newFilter) => {
      if (!dataLoaded) {
        console.log('❌ Dashboard not loaded yet, cannot apply filter');
        return;
      }
      
      console.log('🔄 Reloading ALL dashboard sections with new ad name filter:', newFilter);
      console.log('🔄 Current filter state:', adNameFilter);
      console.log('🔄 New filter to apply:', newFilter);
      
      // Update the ad name filter state first
      setAdNameFilter(newFilter);
      
      // Force a small delay to ensure state update completes, then reload
      setTimeout(async () => {
        console.log('🔄 Triggering dashboard reload after state update...');
        console.log('🔄 Selected range:', selectedRange);
        
        // Call loadDashboardData with the new filter directly as a parameter
        // This bypasses the state update timing issue
        try {
          await loadDashboardData(selectedRange, null, null, newFilter);
          console.log('🔄 Dashboard reload completed successfully with new filter');
        } catch (error) {
          console.error('🔄 Error during dashboard reload:', error);
        }
      }, 100);
    };

  // Handle ad name filter changes (legacy - keeping for compatibility)
  const handleAdNameFilterChange = (newFilter) => {
    // Ensure the filter always has a valid structure, even when cleared
    const safeFilter = newFilter && newFilter.keywords && newFilter.keywords.length > 0 
      ? newFilter 
      : { keywords: [], logic: 'AND' };
    
    setAdNameFilter(safeFilter);
    
    // Reset all loading states immediately when filter changes
    setSectionLoading(prev => ({
      ...prev,
      metrics: true,
      trends: true,
      topContent: true,
      adTypes: true,
      highestSpendingFeatures: true
    }));
    
    // Reload top performing content when filter changes, but skip if configured
    if (dataLoaded && !isTopPerformingConfigured) {
      if (dateRangeType === 'custom' && startDate && endDate) {
        loadDashboardData(selectedRange, startDate, endDate);
      } else {
        loadDashboardData(selectedRange);
      }
    }
  };

  // Safely clear ad name filter
  const clearAdNameFilter = () => {
    const emptyFilter = { keywords: [], logic: 'AND' };
    setAdNameFilter(emptyFilter);
    
    // Reset all loading states immediately when filter is cleared
    setSectionLoading(prev => ({
      ...prev,
      metrics: true,
      trends: true,
      topContent: true,
      adTypes: true,
      highestSpendingFeatures: true
    }));
    
    // Reload dashboard data when filter is cleared
    if (dataLoaded && !isTopPerformingConfigured) {
      if (dateRangeType === 'custom' && startDate && endDate) {
        loadDashboardData(selectedRange, startDate, endDate);
      } else {
        loadDashboardData(selectedRange);
      }
    }
  };

  const getPresetRanges = () => [
    { label: 'Last 7 days', days: 7, color: 'from-blue-500 to-cyan-500' },
    { label: 'Last 30 days', days: 30, color: 'from-purple-500 to-pink-500' },
    { label: 'Last 90 days', days: 90, color: 'from-green-500 to-emerald-500' },
    { label: 'Last 180 days', days: 180, color: 'from-orange-500 to-red-500' },
    { label: 'Last 365 days', days: 365, color: 'from-indigo-500 to-purple-500' }
  ];

  const formatDateRange = () => {
    let dateRange = '';
    if (dateRangeType === 'custom' && startDate && endDate) {
      // Parse dates consistently by creating them at local midnight to avoid timezone issues
      const start = new Date(startDate + 'T00:00:00');
      const end = new Date(endDate + 'T00:00:00');
      dateRange = `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      dateRange = `Last ${selectedRange} days`;
    }
    
    // Add report type info
    const reportTypeName = REPORT_TYPE_NAMES[activeDashboardTab] || activeDashboardTab;
    dateRange += ` • ${reportTypeName}`;
    
    // Add correlation metric info for tabs that use correlation analysis
    // Include all group-by options and existing tabs
    const tabsWithCorrelation = [
      'creative-attributes', 'top-performing', 'ad-type-analysis', 'copy-theme-analysis', 
      'creative-breakdown', 'media-library',
      // New group-by options
      'asset-id', 'campaign-name', 'concept', 'hook', 'ad-type', 'value-proposition', 
      'content-category', 'talent-quantity', 'talent-gender', 'talent-age', 'setting', 
      'music-playing', 'music-genre', 'voiceover', 'dominant-color', 'video-length'
    ];
    if (tabsWithCorrelation.includes(activeDashboardTab)) {
      const metricDisplay = correlationMetric.toUpperCase();
      dateRange += ` • Correlation: ${metricDisplay}`;
    }
    
    // Add ad name filter info if keywords are set
    const validKeywords = adNameFilter.keywords.filter(keyword => keyword && keyword.trim());
    if (validKeywords.length > 0) {
      const modeText = adNameFilter.mode === 'exclude' ? 'Exclude' : 'Include';
      const modeIcon = adNameFilter.mode === 'exclude' ? '❌' : '✅';
      dateRange += ` • ${modeIcon} ${modeText}: ${adNameFilter.logic} (${validKeywords.join(', ')})`;
    }
    
    return dateRange;
  };

  // Progress bar animation functions
  const startDashboardProgress = (duration = 20000) => {
    setDashboardLoadingProgress(0);
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / duration) * 100, 95); // Cap at 95% until completion
      setDashboardLoadingProgress(progress);
      
      if (elapsed >= duration) {
        clearInterval(interval);
      }
    }, 100);
    
    return interval;
  };

  const startChatProgress = (duration = 20000) => {
    setChatLoadingProgress(0);
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / duration) * 100, 95); // Cap at 95% until completion
      setChatLoadingProgress(progress);
      
      if (elapsed >= duration) {
        clearInterval(interval);
      }
    }, 100);
    
    return interval;
  };

  const completeDashboardProgress = () => {
    setDashboardLoadingProgress(100);
    setTimeout(() => {
      setDashboardLoadingProgress(0);
    }, 500);
  };

  const completeChatProgress = () => {
    setChatLoadingProgress(100);
    setTimeout(() => {
      setChatLoadingProgress(0);
    }, 500);
  };

  // Handle chat input changes with auto-expansion
  const handleInputChange = (e) => {
    const text = e.target.value;
    setInputMessage(text);
    
    // Auto-expand if text is long (>200 chars) or has multiple lines
    const shouldExpand = text.length > 200 || text.includes('\n');
    setIsTextboxExpanded(shouldExpand);
  };

  // Handle keyboard events for Shift+Enter support
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Shift+Enter: Allow default behavior (newline)
        return;
      } else {
        // Enter only: Send message
        e.preventDefault();
        handleSendMessage();
      }
    }
  };

  // Handle editing a message
  const handleEditMessage = (messageId, currentText) => {
    setEditingMessageId(messageId);
    setEditingText(currentText);
    
    // Auto-resize the textarea after it's rendered
    setTimeout(() => {
      const textarea = document.querySelector('textarea[value="' + currentText + '"]');
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
      }
    }, 100);
  };

  // Handle saving edited message
  const handleSaveEdit = async () => {
    if (!editingText.trim()) return;

    // Update the message in chat
    setChatMessages(prev => prev.map(msg => 
      msg.id === editingMessageId 
        ? { ...msg, content: editingText }
        : msg
    ));

    // Send the edited message
    const userMessage = { id: Date.now(), role: 'user', content: editingText };
    setChatMessages(prev => [...prev, userMessage]);
    setEditingMessageId(null);
    setEditingText('');
    setIsTyping(true);

    // Process the edited message
    await processMessage(editingText);
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingText('');
  };

  // Process a message (extracted from handleSendMessage)
  const processMessage = async (messageText) => {
    // Start progress bar for AI response
    const progressInterval = startChatProgress(30000); // 30 seconds

    try {
      // Prepare chat history for context (last 10 messages)
      const chatHistory = chatMessages.slice(-10).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Get current date range information for the AI
      let dateRangeInfo = null;
      if (dateRangeType === 'custom' && startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        dateRangeInfo = {
          type: 'custom',
          startDate: start.toISOString().split('T')[0],
          endDate: end.toISOString().split('T')[0],
          description: `from ${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} to ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
        };
      } else {
        dateRangeInfo = {
          type: 'preset',
          days: selectedRange,
          description: `in the last ${selectedRange} days`
        };
      }

      const response = await axios.post('/api/chat/message', {
        message: messageText,
        chatHistory: chatHistory,
        dateRange: dateRangeInfo,
        accountId: getAccountId(),
        enableVisualization: true // Enable smart visualization recommendations
      });

      if (response.data.success) {
        const aiMessage = { 
          id: Date.now() + 1, 
          role: 'assistant', 
          content: response.data.explanation,
          data: response.data.data,
          sqlQuery: response.data.sqlQuery,
          columns: response.data.columns,
          rowCount: response.data.rowCount,
          assetUrls: response.data.assetUrls || [],
          followUpQuestions: response.data.followUpQuestions || [],
          // Smart visualization fields
          visualizationType: response.data.visualizationType || 'table',
          visualizationTitle: response.data.visualizationTitle,
          visualizationDescription: response.data.visualizationDescription,
          // Store the user's question that led to this response
          userQuestion: messageText
        };
        setChatMessages(prev => [...prev, aiMessage]);
        toast.success('Query executed successfully!');
      } else {
        const errorMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: response.data.message || 'Sorry, I couldn\'t process your request.',
          error: true,
          suggestions: response.data.suggestions || []
        };
        setChatMessages(prev => [...prev, errorMessage]);
        toast.error('Failed to process query');
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        error: true
      };
      setChatMessages(prev => [...prev, errorMessage]);
      toast.error('Network error occurred');
    } finally {
      clearInterval(progressInterval);
      completeChatProgress();
      setIsTyping(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = { id: Date.now(), role: 'user', content: inputMessage };
    setChatMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTextboxExpanded(false); // Reset expansion when sending
    setIsTyping(true);

    // Process the message
    await processMessage(inputMessage);
  };

  // Function to load only metrics (for configuration changes)
  const loadMetricsOnly = useCallback(async () => {
    console.log('loadMetricsOnly function called');
    console.log('Current dashboardConfig:', dashboardConfig);
    setSectionLoading(prev => ({ ...prev, metrics: true }));
    setDashboardLoadingProgress(15);
    
    // Get dynamic account ID
    const accountId = getAccountId();
    
    try {
      // Handle custom date ranges vs preset ranges
      let currentStart, currentEnd, previousStart, previousEnd;
      
      if (dateRangeType === 'custom' && startDate && endDate) {
        // Custom date range: use actual dates
        // Parse dates consistently by creating them at local midnight to avoid timezone issues
        const start = new Date(startDate + 'T00:00:00');
        const end = new Date(endDate + 'T00:00:00');
        const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
        
        // Format dates for SQL - preserve local date without timezone conversion
        const startDateStr = start.toLocaleDateString('en-CA'); // YYYY-MM-DD format
        const endDateStr = end.toLocaleDateString('en-CA'); // YYYY-MM-DD format
        
        currentStart = `'${startDateStr}'`;
        currentEnd = `'${endDateStr}'`;
        
        // Calculate previous period (same duration before the custom start date)
        const previousStartDate = new Date(start.getTime() - (daysDiff * 24 * 60 * 60 * 1000)); // daysDiff days before start
        const previousEndDate = new Date(start.getTime() - (24 * 60 * 60 * 1000)); // Day before start
        previousStart = `'${previousStartDate.toLocaleDateString('en-CA')}'`;
        previousEnd = `'${previousEndDate.toLocaleDateString('en-CA')}'`;
      } else {
        // Preset range: use days from current date
        // Use timezone-aware CURRENT_DATE to match time series chart
        currentStart = `(CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '${selectedRange} days'`;
        currentEnd = null;
        previousStart = `(CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '${selectedRange * 2} days'`;
        previousEnd = `(CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '${selectedRange} days'`;
      }
      

      
      const metricMappings = {
        totalImpressions: 'SUM(impressions) as total_impressions',
        totalSpend: 'SUM(spend) as total_spend',
        totalClicks: 'SUM(clicks) as total_clicks',
        totalConversions: 'SUM(purchases) as total_conversions',
        totalRevenue: 'SUM(purchase_value) as total_revenue',
        avgCTR: 'ROUND((SUM(clicks)::float / NULLIF(SUM(impressions), 0) * 100)::numeric, 2) as avg_ctr',
        avgCPC: 'ROUND((SUM(spend)::float / NULLIF(SUM(clicks), 0))::numeric, 2) as avg_cpc',
        avgROAS: 'ROUND((SUM(purchase_value)::float / NULLIF(SUM(spend), 0))::numeric, 2) as avg_roas',
        avgCPA: 'ROUND((SUM(spend)::float / NULLIF(SUM(purchases), 0))::numeric, 2) as avg_cpa',
        avgCVR: 'ROUND((SUM(purchases)::float / NULLIF(SUM(clicks), 0) * 100)::numeric, 2) as avg_cvr',
        // Use existing calculated columns from database
        videoViews: 'SUM(sum_video_p100) as video_views',
        videoCompletions: 'ROUND((SUM(sum_video_p100)::float / NULLIF(SUM(sum_video_p25), 0) * 100)::numeric, 2) as video_completions'
        // Remove reach and frequency as they don't exist in the database
        // reach: 'SUM(reach) as reach',
        // frequency: 'ROUND((SUM(impressions)::float / NULLIF(SUM(reach), 0))::numeric, 2) as frequency'
      };
      
      console.log('loadMetricsOnly - Starting metrics fetch...');
      
      const metricsResponse = await dashboardService.getMetrics(accountId, currentStart, currentEnd);
      
      console.log('loadMetricsOnly - Response:', metricsResponse.data);
      
      // Get previous period data for growth calculation
      const previousMetricsResponse = await dashboardService.getPreviousMetrics(accountId, previousStart, previousEnd);
      
      console.log('loadMetricsOnly - Previous period response:', previousMetricsResponse.data);
      
      // Process current period metrics
      let currentMetrics = {};
      if (metricsResponse.success && metricsResponse.data && metricsResponse.data.length > 0) {
        const row = metricsResponse.data[0];
        Object.keys(row).forEach(key => {
          if (key === 'active_ads') return;
          const value = row[key];
          if (typeof value === 'number' || !isNaN(parseFloat(value))) {
            currentMetrics[key] = parseFloat(value) || 0;
          } else {
            currentMetrics[key] = value || 0;
          }
        });
      }
      
      // Process previous period metrics
      let previousMetrics = {};
      if (previousMetricsResponse.success && previousMetricsResponse.data && previousMetricsResponse.data.length > 0) {
        const row = previousMetricsResponse.data[0];
        Object.keys(row).forEach(key => {
          if (key === 'active_ads') return;
          const value = row[key];
          if (typeof value === 'number' || !isNaN(parseFloat(value))) {
            previousMetrics[key] = parseFloat(value) || 0;
          } else {
            previousMetrics[key] = value || 0;
          }
        });
      }
      
      // Create metrics object with proper formatting - only for enabled metrics
      const metricsObject = {};
      
      // Get the enabled metric keys from dashboardConfig
      const enabledMetricKeys = Object.entries(dashboardConfig.metrics)
        .filter(([key, enabled]) => enabled)
        .map(([key]) => key);
      
      console.log('loadMetricsOnly - Enabled metric keys:', enabledMetricKeys);
      
      // Only process metrics that are enabled in the configuration
      enabledMetricKeys.forEach(metricKey => {
        // Map the frontend metric key to the database column name
        const metricMappings = {
          totalImpressions: 'total_impressions',
          totalSpend: 'total_spend', 
          totalClicks: 'total_clicks',
          totalConversions: 'total_conversions',
          totalRevenue: 'total_revenue',
          avgCTR: 'avg_ctr',
          avgCPC: 'avg_cpc',
          avgROAS: 'avg_roas',
          avgCPA: 'avg_cpa',
          avgCVR: 'avg_cvr'
        };
        
        const dbKey = metricMappings[metricKey];
        if (!dbKey || currentMetrics[dbKey] === undefined) {
          console.log(`loadMetricsOnly - Skipping ${metricKey} (no data)`);
          return;
        }
        
                          const value = currentMetrics[dbKey];
         let camelKey = dbKey.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
         
         // Handle acronyms properly for frontend compatibility (same as initial loading)
         if (camelKey === 'avgCtr') camelKey = 'avgCTR';
         if (camelKey === 'avgCpc') camelKey = 'avgCPC';
         if (camelKey === 'avgRoas') camelKey = 'avgROAS';
         if (camelKey === 'avgCpa') camelKey = 'avgCPA';
         if (camelKey === 'avgCvr') camelKey = 'avgCVR';
         
         console.log(`loadMetricsOnly - Processing metric: ${dbKey} -> ${camelKey}, value: ${value}, type: ${typeof value}`);
         
         // Format the value based on the metric type (same logic as initial loading)
         if (dbKey.includes('total_')) {
           if (dbKey === 'total_spend' || dbKey === 'total_revenue') {
             metricsObject[camelKey] = formatCurrency(value);
           } else {
             metricsObject[camelKey] = formatNumber(value);
           }
         } else if (dbKey.includes('avg_') && (dbKey.includes('ctr') || dbKey.includes('cvr'))) {
           console.log(`loadMetricsOnly - Processing CTR/CVR metric: ${dbKey}, value: ${value}`);
           metricsObject[camelKey] = value === 0 ? '0%' : `${value}%`;
         } else if (dbKey.includes('roas')) {
           metricsObject[camelKey] = value === 0 ? '0.00' : value.toFixed(2);
         } else if (dbKey.includes('cpc') || dbKey.includes('cpa')) {
           metricsObject[camelKey] = formatCurrency(value);
         } else {
           metricsObject[camelKey] = formatNumber(value);
         }
        
        // Add growth placeholder (will be updated later) - same logic as initial loading
        let growthKey;
        if (dbKey.includes('total_')) {
          growthKey = camelKey.replace('total', '') + 'Growth';
        } else if (dbKey.includes('avg_')) {
          growthKey = camelKey + 'Growth';
        } else {
          growthKey = camelKey + 'Growth';
        }
        metricsObject[growthKey] = '+0%';
      });
      
      // Calculate period-over-period changes
      const calculateGrowth = (current, previous) => {
        if (!previous || previous === 0) return '+0%';
        const change = ((current - previous) / previous) * 100;
        const result = `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
        console.log(`calculateGrowth(${current}, ${previous}) = ${result}`);
        return result;
      };

      // Update metrics with growth calculations
      const updatedMetrics = { ...metricsObject };
      
      // Calculate growth for basic metrics that we have from previous period
      const basicMetrics = ['total_impressions', 'total_spend', 'total_clicks', 'total_conversions', 'total_revenue'];
      basicMetrics.forEach(key => {
        if (currentMetrics[key] !== undefined) {
          const currentValue = currentMetrics[key];
          const previousValue = previousMetrics[key] || 0;
          // Convert snake_case to camelCase with proper acronym handling
          let camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
          
          // Handle acronyms properly for frontend compatibility
          if (camelKey === 'avgCtr') camelKey = 'avgCTR';
          if (camelKey === 'avgCpc') camelKey = 'avgCPC';
          if (camelKey === 'avgRoas') camelKey = 'avgROAS';
          if (camelKey === 'avgCpa') camelKey = 'avgCPA';
          if (camelKey === 'avgCvr') camelKey = 'avgCVR';
          
          const growthKey = camelKey.replace('total', '').replace('avg', '') + 'Growth';
          updatedMetrics[growthKey] = calculateGrowth(currentValue, previousValue);
        }
      });
      
      // For calculated metrics (avg_ctr, avg_cpc, avg_roas, avg_cpa, avg_cvr), 
      // we need to calculate them from the basic metrics
      if (currentMetrics.total_impressions && currentMetrics.total_clicks && previousMetrics.total_impressions && previousMetrics.total_clicks) {
        const currentCTR = (currentMetrics.total_clicks / currentMetrics.total_impressions) * 100;
        const previousCTR = (previousMetrics.total_clicks / previousMetrics.total_impressions) * 100;
        console.log('CTR Growth calculation:', { currentCTR, previousCTR, currentClicks: currentMetrics.total_clicks, currentImpressions: currentMetrics.total_impressions, previousClicks: previousMetrics.total_clicks, previousImpressions: previousMetrics.total_impressions });
        updatedMetrics.avgCTRGrowth = calculateGrowth(currentCTR, previousCTR);
      }
      
      if (currentMetrics.total_spend && currentMetrics.total_clicks && previousMetrics.total_spend && previousMetrics.total_clicks) {
        const currentCPC = currentMetrics.total_spend / currentMetrics.total_clicks;
        const previousCPC = previousMetrics.total_spend / previousMetrics.total_clicks;
        console.log('CPC Growth calculation:', { currentCPC, previousCPC, currentSpend: currentMetrics.total_spend, currentClicks: currentMetrics.total_clicks, previousSpend: previousMetrics.total_spend, previousClicks: previousMetrics.total_clicks });
        updatedMetrics.avgCPCGrowth = calculateGrowth(currentCPC, previousCPC);
      }
      
      if (currentMetrics.total_revenue && currentMetrics.total_spend && previousMetrics.total_revenue && previousMetrics.total_spend) {
        const currentROAS = currentMetrics.total_revenue / currentMetrics.total_spend;
        const previousROAS = previousMetrics.total_revenue / previousMetrics.total_spend;
        console.log('ROAS Growth calculation:', { currentROAS, previousROAS, currentRevenue: currentMetrics.total_revenue, currentSpend: currentMetrics.total_spend, previousRevenue: previousMetrics.total_revenue, previousSpend: previousMetrics.total_spend });
        updatedMetrics.avgROASGrowth = calculateGrowth(currentROAS, previousROAS);
      }
      
      if (currentMetrics.total_spend && currentMetrics.total_conversions && previousMetrics.total_spend && previousMetrics.total_conversions) {
        const currentCPA = currentMetrics.total_spend / currentMetrics.total_conversions;
        const previousCPA = previousMetrics.total_spend / previousMetrics.total_conversions;
        console.log('CPA Growth calculation:', { currentCPA, previousCPA, currentSpend: currentMetrics.total_spend, currentConversions: currentMetrics.total_conversions, previousSpend: previousMetrics.total_spend, previousConversions: previousMetrics.total_conversions });
        updatedMetrics.avgCPAGrowth = calculateGrowth(currentCPA, previousCPA);
      }
      
      if (currentMetrics.total_clicks && currentMetrics.total_conversions && previousMetrics.total_clicks && previousMetrics.total_conversions) {
        const currentCVR = (currentMetrics.total_conversions / currentMetrics.total_clicks) * 100;
        const previousCVR = (previousMetrics.total_conversions / previousMetrics.total_clicks) * 100;
        console.log('CVR Growth calculation:', { currentCVR, previousCVR, currentConversions: currentMetrics.total_conversions, currentClicks: currentMetrics.total_clicks, previousConversions: previousMetrics.total_conversions, previousClicks: previousMetrics.total_clicks });
        updatedMetrics.avgCVRGrowth = calculateGrowth(currentCVR, previousCVR);
      }
      
      console.log('Final updatedMetrics with growth values:', updatedMetrics);
      
      setDashboardData(prev => ({
        ...prev,
        metrics: updatedMetrics
      }));
      
    } catch (error) {
      console.log('Metrics loading failed:', error);
    } finally {
      setSectionLoading(prev => ({ ...prev, metrics: false }));
      setDashboardLoadingProgress(100);
    }
  }, [selectedRange, dateRangeType, startDate, endDate, adNameFilter]);

  const handleConfigSave = useCallback((newConfig) => {
    console.log('Config save triggered with:', newConfig);
    
    // Only reload metrics if the configuration actually changed
    const hasChanges = JSON.stringify(newConfig) !== JSON.stringify(dashboardConfig);
    
    setDashboardConfig(newConfig);
    setShowConfig(false);
    
    if (hasChanges) {
      console.log('Configuration changed, calling loadMetricsOnly in 100ms...');
      setTimeout(() => { 
        console.log('Executing loadMetricsOnly...');
        loadMetricsOnly(); 
      }, 100);
    } else {
      console.log('No configuration changes detected, skipping metrics reload');
    }
  }, [loadMetricsOnly, dashboardConfig]);

  const getEnabledMetrics = () => {
    return Object.entries(dashboardConfig.metrics)
      .filter(([key, enabled]) => enabled)
      .map(([key]) => key);
  };


  // Chart hiding functions
  const hideAllCharts = () => {
    const allChartIds = ['performance_trends', 'ad_types', 'features', 'top_content', 'key_metrics'];
    setHiddenCharts(new Set(allChartIds));
    
    // Add hidden class to all chart elements
    allChartIds.forEach(chartId => {
      const chartElement = document.querySelector(`[data-chart-id="${chartId}"]`);
      if (chartElement) {
        chartElement.classList.add('hidden');
      }
    });
    
    toast.success('All dashboard charts hidden!', {
      icon: '👁️',
      duration: 2000
    });
  };

  const showAllCharts = () => {
    setHiddenCharts(new Set());
    
    // Remove hidden class from all chart elements
    document.querySelectorAll('[data-chart-id]').forEach(element => {
      element.classList.remove('hidden');
    });
    
    toast.success('All dashboard charts restored!', {
      icon: '📊',
      duration: 2000
    });
  };



  const handleMetricClick = (metric, data) => {
    // Generate relevant query based on the metric clicked
    let relevantQuery = '';
    
    // Get the proper date range description
    const getDateRangeDescription = () => {
      if (dateRangeType === 'custom' && startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        return `from ${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} to ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      } else {
        return `in the last ${selectedRange} days`;
      }
    };
    
    const dateRangeDesc = getDateRangeDescription();
    
    switch (metric) {
      case 'total_impressions':
        relevantQuery = `Show me top 10 ads by impressions ${dateRangeDesc}`;
        break;
      case 'total_spend':
        relevantQuery = `Show me spend breakdown by campaign and ad set ${dateRangeDesc}`;
        break;
      case 'total_clicks':
        relevantQuery = `Show me ads with highest click-through rates ${dateRangeDesc}`;
        break;
      case 'total_conversions':
        relevantQuery = `Show me top performing ads by conversions and ROAS ${dateRangeDesc}`;
        break;
      case 'total_revenue':
        relevantQuery = `Show me revenue breakdown by campaign and ad set ${dateRangeDesc}`;
        break;
      case 'avg_ctr':
        relevantQuery = `Show me ads with highest click-through rates and analyze CTR trends ${dateRangeDesc}`;
        break;
      case 'avg_cpc':
        relevantQuery = `Show me cost per click analysis and identify cost-effective campaigns ${dateRangeDesc}`;
        break;
      case 'avg_roas':
        relevantQuery = `Show me return on ad spend analysis and identify high-ROAS campaigns ${dateRangeDesc}`;
        break;
      case 'avg_cpa':
        relevantQuery = `Show me cost per acquisition analysis and identify efficient conversion campaigns ${dateRangeDesc}`;
        break;
      case 'avg_cvr':
        relevantQuery = `Show me conversion rate analysis and identify high-converting ads ${dateRangeDesc}`;
        break;
      // Removed reach and frequency cases as they don't exist in the database
      case 'performance_trends':
        relevantQuery = `Analyze daily performance trends and identify patterns ${dateRangeDesc}`;
        break;

      case 'top_content':
        relevantQuery = `Show me detailed analysis of top 10 performing ads with creative insights ${dateRangeDesc}`;
        break;
      case 'ad_types':
        relevantQuery = `Compare performance across different ad types (VIDEO, IMAGE, CAROUSEL) including impressions, spend, CTR, and ROAS ${dateRangeDesc}`;
        break;
      case 'fast_growing_features':
        relevantQuery = `Show me detailed analysis of fast-growing creative features including visual styles, text overlays, audio elements, and brand presence ${dateRangeDesc}`;
        break;
      default:
        if (metric.startsWith('content_')) {
          // Use the data object passed to the click handler instead of array index
          const content = data;
          // Extract raw numeric values for better AI prompts
          const rawCTR = content.engagement ? parseFloat(content.engagement.replace('%', '')) : 0;
          const rawROAS = content.roas ? parseFloat(content.roas.replace('x', '')) : 0;
          relevantQuery = `Show me specific creative examples of ads with name containing "${content.rawTitle}" including their creative elements, performance metrics (${content.views} impressions, ${rawCTR}% CTR, ${rawROAS}x ROAS), and detailed analysis ${dateRangeDesc}`;
        } else if (metric.startsWith('ad_type_')) {
          // Use the data object passed to the click handler instead of array index
          const adType = data;
          // Extract raw numeric values for better AI prompts
          const rawCTR = adType.ctr ? parseFloat(adType.ctr.replace('%', '')) : 0;
          const rawROAS = adType.roas ? parseFloat(adType.roas.replace('x', '')) : 0;
          relevantQuery = `Show me specific creative examples of ${adType.rawType} ads with their creative elements, performance metrics (${adType.count} ads, ${adType.impressions} impressions, ${adType.spend} spend, ${rawCTR}% CTR, ${rawROAS}x ROAS) ${dateRangeDesc}`;
        } else if (metric.startsWith('feature_')) {
          // Use the data object passed to the click handler instead of array index
          const feature = data;
          // New format: "Category: Feature: Value" (e.g., "Visual: Product Position: Center")
          const parts = feature.feature.split(':');
          const featureCategory = parts[0]?.trim(); // e.g., "Visual"
          const featureName = parts[1]?.trim(); // e.g., "Product Position"
          const featureValue = parts[2]?.trim() || feature.rawFeature; // e.g., "Center"
          // Extract raw numeric values for better AI prompts
          const rawCTR = feature.ctr ? parseFloat(feature.ctr.replace('%', '')) : 0;
          const rawROAS = feature.roas ? parseFloat(feature.roas.replace('x', '')) : 0;
          relevantQuery = `Show me specific creative examples with ${featureCategory} ${featureName} containing "${featureValue}" including their creative elements, performance metrics (${feature.count} ads, ${feature.impressions} impressions, ${rawCTR}% CTR, ${feature.spend} spend, ${rawROAS}x ROAS), and detailed analysis ${dateRangeDesc}`;
        } else {
          relevantQuery = `Show me overall campaign performance metrics ${dateRangeDesc}`;
        }
    }

    // Auto-populate the chat input with the relevant query
    setInputMessage(relevantQuery);
  };

  // Handle content query submission from configuration modal
  const handleContentQuerySubmit = async (query) => {
    if (!query.trim()) return;

    try {
      // Set loading state for top content section
      setSectionLoading(prev => ({ ...prev, topContent: true }));

      // Add the query to chat
      const newMessage = {
        id: Date.now(),
        role: 'user',
        content: query.trim()
      };
      setChatMessages(prev => [...prev, newMessage]);

      // Process the query to get new top performing ads data
      // Note: Custom queries bypass the ad name filter to avoid structure conflicts
      const response = await fetch('/api/chat/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: query.trim(),
          accountId: getAccountId(),
          dateRange: {
            type: dateRangeType,
            days: selectedRange,
            startDate: startDate,
            endDate: endDate
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // Update the top content data with the new results
        if (result.data && result.data.length > 0) {
          setDashboardData(prev => ({
            ...prev,
            topContent: result.data
          }));
          
          // Reset sorting to default since new data structure might be different
          setContentSort({ field: 'roas', order: 'desc' });
          
          // Mark that this is custom query data to prevent ad name filter conflicts
          setDashboardData(prev => ({
            ...prev,
            isCustomTopContent: true
          }));
          
          // Mark top performing section as configured by user
          setIsTopPerformingConfigured(true);
          // Store the configured query to use as title
          setConfiguredTopContentQuery(query.trim());
        }
        
        // Add AI response to chat
        if (result.aiResponse) {
          const aiMessage = {
            id: Date.now() + 1,
            role: 'assistant',
            content: result.aiResponse
          };
          setChatMessages(prev => [...prev, aiMessage]);
        }
      }

      // Close the config modal
      setShowContentConfig(false);
      
    } catch (error) {
      console.error('Error processing content query:', error);
      // Add error message to chat
      const errorMessage = {
        id: Date.now(),
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your query. Please try again.'
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      // Clear loading state
      setSectionLoading(prev => ({ ...prev, topContent: false }));
    }
  };

  // Ad Type Table Configuration Modal
  const AdTypeConfigModal = () => {
    if (!showAdTypeConfig || sectionLoading.adTypes) return null;

    const availableMetrics = [
      { key: 'spend', label: 'Spend', icon: '💰' },
      { key: 'impressions', label: 'Impressions', icon: '👁️' },
      { key: 'ctr', label: 'CTR', icon: '📊' },
      { key: 'roas', label: 'ROAS', icon: '📈' },
      { key: 'count', label: 'Ad Count', icon: '📋' }
    ];

    const viewModes = [
      { key: 'cards', label: 'Cards View', icon: '🃏' },
      { key: 'table', label: 'Table View', icon: '📋' }
    ];



    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-white">Ad Type Performance Table Configuration</h3>
            <button
              onClick={() => setShowAdTypeConfig(false)}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-slate-400" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Loading State */}
            {sectionLoading.adTypes && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-purple-400 mr-3" />
                <span className="text-slate-400">Loading ad types data...</span>
              </div>
            )}

            {/* View Mode Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-3">View Mode</label>
              <div className="grid grid-cols-3 gap-3">
                {viewModes.map((mode) => (
                  <button
                    key={mode.key}
                    onClick={() => setAdTypeTableConfig(prev => ({ ...prev, viewMode: mode.key }))}
                    disabled={sectionLoading.adTypes}
                    className={`p-3 rounded-lg border transition-all ${
                      adTypeTableConfig.viewMode === mode.key
                        ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                        : 'border-slate-600 hover:border-slate-500 text-slate-400 hover:text-slate-300'
                    } ${sectionLoading.adTypes ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="text-lg mb-1">{mode.icon}</div>
                    <div className="text-sm font-medium">{mode.label}</div>
                  </button>
                ))}
              </div>
            </div>



            {/* Metrics Selection */}
            {adTypeTableConfig.viewMode !== 'pivot' && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-3">Display Metrics</label>
                <div className="grid grid-cols-2 gap-2">
                  {availableMetrics.map((metric) => (
                    <label key={metric.key} className={`flex items-center space-x-2 p-2 rounded-lg hover:bg-slate-700 cursor-pointer ${
                      sectionLoading.adTypes ? 'opacity-50 cursor-not-allowed' : ''
                    }`}>
                      <input
                        type="checkbox"
                        checked={adTypeTableConfig.showMetrics.includes(metric.key)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAdTypeTableConfig(prev => ({
                              ...prev,
                              showMetrics: [...prev.showMetrics, metric.key]
                            }));
                          } else {
                            setAdTypeTableConfig(prev => ({
                              ...prev,
                              showMetrics: prev.showMetrics.filter(m => m !== metric.key)
                            }));
                          }
                        }}
                        disabled={sectionLoading.adTypes}
                        className="rounded border-slate-600 bg-slate-700 text-purple-500 focus:ring-purple-500"
                      />
                      <span className="text-sm text-slate-300">{metric.icon} {metric.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Max Items */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Maximum Items to Show</label>
              <input
                type="number"
                min="1"
                max="20"
                value={adTypeTableConfig.maxItems}
                onChange={(e) => setAdTypeTableConfig(prev => ({ ...prev, maxItems: parseInt(e.target.value) }))}
                disabled={sectionLoading.adTypes}
                className={`w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-purple-500 focus:outline-none ${
                  sectionLoading.adTypes ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              />
            </div>



            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-700">
              <button
                onClick={() => setShowAdTypeConfig(false)}
                disabled={sectionLoading.adTypes}
                className={`px-4 py-2 transition-colors ${
                  sectionLoading.adTypes 
                    ? 'text-slate-600 cursor-not-allowed' 
                    : 'text-slate-400 hover:text-slate-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowAdTypeConfig(false);
                  // Here you could save the config to localStorage or send to backend
                }}
                disabled={sectionLoading.adTypes}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  sectionLoading.adTypes 
                    ? 'bg-slate-600 text-slate-400 cursor-not-allowed' 
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                Apply Configuration
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Features Table Configuration Modal
  const FeaturesConfigModal = () => {
    if (!showFeaturesConfig || sectionLoading.highestSpendingFeatures) return null;

    const availableMetrics = [
      { key: 'count', label: 'Count', icon: '📋' },
      { key: 'spend', label: 'Spend', icon: '💰' },
      { key: 'impressions', label: 'Impressions', icon: '👁️' },
      { key: 'ctr', label: 'CTR', icon: '📊' },
      { key: 'roas', label: 'ROAS', icon: '📈' }
    ];

    const viewModes = [
      { key: 'cards', label: 'Cards View', icon: '🃏' },
      { key: 'table', label: 'Table View', icon: '📋' }
    ];

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-white">Highest Spending Features Configuration</h3>
            <button
              onClick={() => setShowFeaturesConfig(false)}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-slate-400" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Loading State */}
            {sectionLoading.highestSpendingFeatures && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-purple-400 mr-3" />
                <span className="text-slate-400">Loading features data...</span>
              </div>
            )}

            {/* View Mode Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-3">View Mode</label>
              <div className="grid grid-cols-2 gap-3">
                {viewModes.map((mode) => (
                  <button
                    key={mode.key}
                    onClick={() => setFeaturesTableConfig(prev => ({ ...prev, viewMode: mode.key }))}
                    disabled={sectionLoading.highestSpendingFeatures}
                    className={`p-3 rounded-lg border transition-all ${
                      featuresTableConfig.viewMode === mode.key
                        ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                        : 'border-slate-600 hover:border-slate-500 text-slate-400 hover:text-slate-300'
                    } ${sectionLoading.highestSpendingFeatures ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="text-lg mb-1">{mode.icon}</div>
                    <div className="text-sm font-medium">{mode.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Metrics Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-3">Display Metrics</label>
              <div className="grid grid-cols-2 gap-2">
                {availableMetrics.map((metric) => (
                  <label key={metric.key} className={`flex items-center space-x-2 p-2 rounded-lg hover:bg-slate-700 cursor-pointer ${
                    sectionLoading.highestSpendingFeatures ? 'opacity-50 cursor-not-allowed' : ''
                  }`}>
                    <input
                      type="checkbox"
                      checked={featuresTableConfig.showMetrics.includes(metric.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFeaturesTableConfig(prev => ({
                            ...prev,
                            showMetrics: [...prev.showMetrics, metric.key]
                          }));
                        } else {
                          setFeaturesTableConfig(prev => ({
                            ...prev,
                            showMetrics: prev.showMetrics.filter(m => m !== metric.key)
                          }));
                        }
                      }}
                      disabled={sectionLoading.highestSpendingFeatures}
                      className="rounded border-slate-600 bg-slate-700 text-purple-500 focus:ring-purple-500"
                    />
                    <span className="text-sm text-slate-300">{metric.icon} {metric.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Max Items Configuration */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Number of Items to Display</label>
              <input
                type="number"
                min="1"
                max="20"
                value={featuresTableConfig.maxItems}
                onChange={(e) => setFeaturesTableConfig(prev => ({ ...prev, maxItems: parseInt(e.target.value) || 4 }))}
                disabled={sectionLoading.highestSpendingFeatures}
                className={`w-full bg-slate-600 border border-slate-500 rounded px-3 py-2 text-white ${
                  sectionLoading.highestSpendingFeatures ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-700">
              <button
                onClick={() => setShowFeaturesConfig(false)}
                disabled={sectionLoading.highestSpendingFeatures}
                className={`px-4 py-2 transition-colors ${
                  sectionLoading.highestSpendingFeatures 
                    ? 'text-slate-600 cursor-not-allowed' 
                    : 'text-slate-400 hover:text-slate-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowFeaturesConfig(false);
                }}
                disabled={sectionLoading.highestSpendingFeatures}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  sectionLoading.highestSpendingFeatures 
                    ? 'bg-slate-600 text-slate-400 cursor-not-allowed' 
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                Apply Configuration
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Content Table Configuration Modal
  const ContentConfigModal = () => {
    if (!showContentConfig || sectionLoading.topContent) return null;

    const viewModes = [
      { key: 'cards', label: 'Cards View', icon: '🃏' },
      { key: 'table', label: 'Table View', icon: '📋' }
    ];

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-white">Top Performing Content Configuration</h3>
            <button
              onClick={() => setShowContentConfig(false)}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-slate-400" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Loading State */}
            {sectionLoading.topContent && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-purple-400 mr-3" />
                <span className="text-slate-400">Loading content data...</span>
              </div>
            )}

            {/* Chat Query Configuration */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-3">
                💬 Customize Top Performing Ads Query
              </label>
              <div className="space-y-3">
                <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
                                      <p className="text-xs text-slate-400 mb-2">
                      Ask questions like: "top 10 ads by ROAS", "ads with spend &gt; $1000", "best performing video ads"
                    </p>
                  <div className="flex space-x-2">
                    <input
                      ref={contentQueryRef}
                      type="text"
                      placeholder="e.g., top 15 ads by ROAS with spend > $500"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          const query = e.target.value.trim();
                          if (query) {
                            handleContentQuerySubmit(query);
                            e.target.value = '';
                          }
                        }
                      }}
                      disabled={sectionLoading.topContent}
                      className="flex-1 bg-slate-600 border border-slate-500 rounded px-3 py-2 text-white text-sm placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      autoFocus
                    />
                    <button
                      onClick={() => {
                        const query = contentQueryRef.current?.value?.trim();
                        if (query) {
                          handleContentQuerySubmit(query);
                          contentQueryRef.current.value = '';
                        }
                      }}
                      disabled={sectionLoading.topContent}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-600 disabled:text-slate-400 text-white rounded-lg transition-colors text-sm font-medium"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* View Mode Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-3">View Mode</label>
              <div className="grid grid-cols-2 gap-3">
                {viewModes.map((mode) => (
                  <button
                    key={mode.key}
                    onClick={() => setContentTableConfig(prev => ({ ...prev, viewMode: mode.key }))}
                    disabled={sectionLoading.topContent}
                    className={`p-3 rounded-lg border transition-all ${
                      contentTableConfig.viewMode === mode.key
                        ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                        : 'border-slate-600 hover:border-slate-500 text-slate-400 hover:text-slate-300'
                    } ${sectionLoading.topContent ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="text-lg mb-1">{mode.icon}</div>
                    <div className="text-sm font-medium">{mode.label}</div>
                  </button>
                ))}
              </div>
            </div>



            {/* Max Items Configuration */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Number of Items to Display</label>
                              <input
                  type="number"
                  min="1"
                  max="50"
                  value={contentTableConfig.maxItems}
                onChange={(e) => setContentTableConfig(prev => ({ ...prev, maxItems: parseInt(e.target.value) || 4 }))}
                disabled={sectionLoading.topContent}
                className={`w-full bg-slate-600 border border-slate-500 rounded px-3 py-2 text-white ${
                  sectionLoading.topContent ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-700">
              <button
                onClick={() => setShowContentConfig(false)}
                disabled={sectionLoading.topContent}
                className={`px-4 py-2 transition-colors ${
                  sectionLoading.topContent 
                    ? 'text-slate-600 cursor-not-allowed' 
                    : 'text-slate-400 hover:text-slate-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowContentConfig(false);
                }}
                disabled={sectionLoading.topContent}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  sectionLoading.topContent 
                    ? 'bg-slate-600 text-slate-400 cursor-not-allowed' 
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                Apply Configuration
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Handle dashboard tab change
  const handleDashboardTabChange = (tabId) => {
    setActiveDashboardTab(tabId);
    
    // Keep date picker open when switching between dashboard tabs that support filtering
    const tabsWithFiltering = ['overview', 'top-performing', 'ad-type-analysis', 'copy-theme-analysis', 'creative-attributes', 'creative-breakdown', 'media-library'];
    if (!tabsWithFiltering.includes(tabId) && showDatePicker) {
      setShowDatePicker(false);
    }
  };

  // Load ALL saved reports for the account (not filtered by tab)
  const loadSavedReports = async () => {
    const accountId = getAccountId();
    if (!accountId) return;
    
    try {
      // Load reports for all dashboard types (including new group-by options)
      const dashboardTypes = [
        'top-performing', 'ad-type-analysis', 'copy-theme-analysis', 'creative-attributes', 'creative-breakdown', 'media-library',
        // New group-by options
        'asset-id', 'campaign-name', 'value-proposition', 'content-category', 
        'talent-quantity', 'talent-gender', 'talent-age', 'setting', 
        'music-playing', 'music-genre', 'voiceover', 'dominant-color', 'video-length'
      ];
      const allReports = [];
      
      for (const dashboardType of dashboardTypes) {
        const response = await fetch(`/api/saved-reports/${accountId}/${dashboardType}`);
        const data = await response.json();
        
        if (data.success && data.reports) {
          allReports.push(...data.reports);
        }
      }
      
      // Sort by created_at (most recent first) - backend already does this, but keeping for consistency
      allReports.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      // Debug: Log report dates to check format
      if (allReports.length > 0) {
        console.log('📁 Sample report data:', {
          report: allReports[0],
          start_date: allReports[0].start_date,
          start_date_type: typeof allReports[0].start_date,
          end_date: allReports[0].end_date,
          end_date_type: typeof allReports[0].end_date
        });
      }
      
      setSavedReports(allReports);
      console.log('📁 Loaded', allReports.length, 'saved reports across all tabs');
      
      // Auto-load the most recent report on initial load (if not already loaded)
      return allReports;
    } catch (error) {
      console.error('Error loading saved reports:', error);
      return []; // Return empty array on error
    }
  };

  // Load saved reports once on mount (not when tab changes)
  useEffect(() => {
    const initializeReports = async () => {
      // Guard against React Strict Mode double-mounting
      if (autoLoadAttemptedRef.current) {
        console.log('📁 Auto-load already attempted (Strict Mode guard), skipping');
        return;
      }
      
      if (hasAutoLoadedReport) {
        console.log('📁 Already attempted auto-load, skipping');
        return;
      }
      
      autoLoadAttemptedRef.current = true; // Mark as attempted immediately
      
      try {
        console.log('📁 Checking for saved reports...');
        const reports = await loadSavedReports();
        
        // Auto-load the most recent report if available
        if (reports && reports.length > 0) {
          console.log('📁 Found', reports.length, 'saved reports! Auto-loading most recent:', reports[0].report_name, '(ID:', reports[0].id, ')');
          // DON'T set hasAutoLoadedReport here - wait for handleLoadReport to complete
          setDataLoaded(true); // Mark as loaded immediately to prevent default data loading
          // Small delay to ensure component is fully mounted
          setTimeout(async () => {
            try {
              console.log('📁 Calling handleLoadReport for auto-load...');
              await handleLoadReport(reports[0].id);
              console.log('📁 handleLoadReport completed successfully');
              // NOW set hasAutoLoadedReport after report is loaded
              setHasAutoLoadedReport(true);
            } catch (error) {
              console.error('📁 Error in auto-load handleLoadReport:', error);
              // If report load fails, allow default data to load
              setHasAutoLoadedReport(true);
              setDataLoaded(false);
              toast.error('Failed to load saved report, using default view');
            }
          }, 300);
        } else {
          console.log('📁 No saved reports found (count:', reports?.length, '), loading default dashboard data');
          setHasAutoLoadedReport(true); // Mark as attempted - this will trigger default data load
        }
      } catch (error) {
        console.error('📁 Error during report initialization:', error);
        // On error, proceed with default data loading
        setHasAutoLoadedReport(true);
      }
    };
    
    if (isAuthenticated) {
      initializeReports();
    }
  }, [isAuthenticated]); // Run when authenticated

  // Generate a suggested report name based on current configuration
  const generateReportName = () => {
    const parts = [];
    
    // Add report type
    const reportTypeName = REPORT_TYPE_NAMES[activeDashboardTab] || activeDashboardTab;
    parts.push(reportTypeName);
    
    // Add date range
    if (dateRangeType === 'custom' && startDate && endDate) {
      const startDateStr = typeof startDate === 'string' ? startDate.split('T')[0] : startDate.toISOString().split('T')[0];
      const endDateStr = typeof endDate === 'string' ? endDate.split('T')[0] : endDate.toISOString().split('T')[0];
      const start = new Date(startDateStr + 'T00:00:00');
      const end = new Date(endDateStr + 'T00:00:00');
      const startFormatted = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const endFormatted = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      parts.push(`${startFormatted}-${endFormatted}`);
    } else {
      parts.push(`${selectedRange}D`);
    }
    
    // Add metric
    parts.push(correlationMetric.toUpperCase());
    
    // Add keywords if any
    const validKeywords = adNameFilter.keywords.filter(k => k && k.trim());
    if (validKeywords.length > 0) {
      const modePrefix = adNameFilter.mode === 'exclude' ? 'Excl' : 'Incl';
      const keywordsStr = validKeywords.join('-');
      parts.push(`${modePrefix}:${keywordsStr}`);
    }
    
    return parts.join('-');
  };

  // Save current filter configuration as a report
  const handleSaveReport = async () => {
    if (!newReportName.trim()) {
      toast.error('Please enter a report name');
      return;
    }
    
    const accountId = getAccountId();
    if (!accountId) return;
    
    try {
      // Build report configuration
      // Ensure reportType is set - use activeDashboardTab which is updated when user selects report type
      const currentReportType = activeDashboardTab || 'top-performing';
      
      const reportConfig = {
        reportName: newReportName.trim(),
        accountId,
        dashboardType: currentReportType,
        reportType: currentReportType, // Report type selected from Report Type tab
        correlationMetric,
        adNameFilter: adNameFilter.keywords.length > 0 ? adNameFilter : null
      };
      
      // Add date range - use custom range if set, otherwise use preset days
      // IMPORTANT: Only one should be set - either date_range_days OR (start_date AND end_date)
      if (dateRangeType === 'custom' && startDate && endDate) {
        // Custom date range: save start and end dates, clear preset days
        reportConfig.startDate = startDate;
        reportConfig.endDate = endDate;
        reportConfig.dateRangeDays = null; // Clear days when using custom range
      } else {
        // Preset date range: save days, clear custom dates
        reportConfig.dateRangeDays = selectedRange;
        reportConfig.startDate = null;
        reportConfig.endDate = null;
      }
      
      console.log('💾 Saving report config:', {
        ...reportConfig,
        dateRangeType,
        hasCustomRange: dateRangeType === 'custom' && startDate && endDate,
        activeDashboardTab, // Show current active tab
        reportType: reportConfig.reportType // Explicitly show reportType being sent
      });
      
      const response = await fetch('/api/saved-reports/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reportConfig)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Report saved successfully!');
        setShowSaveReportModal(false);
        setNewReportName('');
        loadSavedReports(); // Refresh the list
      } else {
        toast.error(data.error || 'Failed to save report');
      }
    } catch (error) {
      console.error('Error saving report:', error);
      toast.error('Failed to save report');
    }
  };

  // Load a saved report
  const handleLoadReport = async (reportId) => {
    // Prevent double-click
    if (loadingReportId === reportId) {
      console.log('📁 Report already loading, ignoring duplicate click');
      return;
    }
    
    try {
      setLoadingReportId(reportId);
      console.log('📁 Loading saved report:', reportId);
      
      const response = await fetch(`/api/saved-reports/report/${reportId}`);
      console.log('📁 Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        console.error('📁 API Error:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('📁 Error response:', errorText);
        toast.error(`Failed to load report: ${response.statusText}`);
        setLoadingReportId(null);
        return;
      }
      
      const data = await response.json();
      console.log('📁 Response data:', data);
      
      if (data.success && data.report) {
        const report = data.report;
        console.log('📁 Report loaded:', report);
        
        // Close the saved reports list immediately for better UX
        setShowSavedReportsList(false);
        
        // Switch to the correct report type if different from current
        const reportType = report.report_type || report.dashboard_type;
        if (reportType && reportType !== activeDashboardTab) {
          console.log('📁 Switching to report type:', reportType);
          handleDashboardTabChange(reportType);
        }
        
        // Apply the saved filters - update all at once
        const newCorrelationMetric = report.correlation_metric || 'roas';
        const newAdNameFilter = report.ad_name_filter || {
          keywords: [],
          logic: 'AND',
          mode: 'include'
        };
        
        // Determine if custom or preset date range
        const hasCustomRange = report.start_date && report.end_date;
        
        // Extract YYYY-MM-DD from ISO timestamps (handles '2025-11-01' and '2025-11-01T07:00:00.000Z')
        let newStartDate = '';
        let newEndDate = '';
        let newDateRange;
        
        if (hasCustomRange) {
          newStartDate = typeof report.start_date === 'string' 
            ? report.start_date.split('T')[0]
            : report.start_date.toISOString().split('T')[0];
          newEndDate = typeof report.end_date === 'string'
            ? report.end_date.split('T')[0]
            : report.end_date.toISOString().split('T')[0];
          
          // Calculate actual days difference for custom range (same as handleCustomDateRangeSubmit)
          const start = new Date(newStartDate + 'T00:00:00');
          const end = new Date(newEndDate + 'T00:00:00');
          newDateRange = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
        } else {
          newDateRange = (report.date_range_days && report.date_range_days > 0) ? report.date_range_days : 7;
        }
        
        const newDateRangeType = hasCustomRange ? 'custom' : 'preset';
        
        console.log('📁 Applying filters:', { 
          dateRangeType: newDateRangeType,
          dateRangeDays: newDateRange,
          startDate: newStartDate,
          endDate: newEndDate,
          hasCustomRange,
          correlationMetric: newCorrelationMetric, 
          adNameFilter: newAdNameFilter
        });
        
        // Update all state immediately - React will batch these
        setDateRangeType(newDateRangeType);
        setSelectedRange(newDateRange);
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        setCorrelationMetric(newCorrelationMetric);
        setAdNameFilter(newAdNameFilter);
        setPendingAdNameFilter({
          keywords: newAdNameFilter.keywords?.length > 0 ? newAdNameFilter.keywords : [''],
          logic: newAdNameFilter.logic || 'AND',
          mode: newAdNameFilter.mode || 'include'
        });
        setDataLoaded(false); // Allow dashboard to reload with new filters
        
        console.log('📁 All filters applied immediately');
        
        // Format date range for toast message (dates already extracted as YYYY-MM-DD)
        let dateRangeDisplay;
        if (hasCustomRange) {
          const startDate = new Date(newStartDate + 'T00:00:00');
          const endDate = new Date(newEndDate + 'T00:00:00');
          dateRangeDisplay = `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
        } else {
          dateRangeDisplay = `${newDateRange}d`;
        }
        
        // Force refresh on next tick after React has committed the state changes
        requestAnimationFrame(() => {
          // Reload dashboard data with the correct parameters
          if (hasCustomRange) {
            loadDashboardData(newDateRange, newStartDate, newEndDate, newAdNameFilter);
          } else {
            loadDashboardData(newDateRange, null, null, newAdNameFilter);
          }
          
          setRefreshTrigger(prev => prev + 1);
          console.log('🔄 Force refresh triggered - all filters applied');
          
          toast.success(`Loaded: ${report.report_name} (${dateRangeDisplay})`);
          setLoadingReportId(null);
        });
      } else {
        setLoadingReportId(null);
      }
    } catch (error) {
      console.error('Error loading report:', error);
      toast.error('Failed to load report');
      setLoadingReportId(null);
    }
  };

  // Delete a saved report
  const handleDeleteReport = async (reportId, reportName) => {
    if (!window.confirm(`Are you sure you want to delete "${reportName}"?`)) {
      return;
    }
    
    try {
      const response = await fetch(`/api/saved-reports/delete/${reportId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Report deleted successfully!');
        loadSavedReports(); // Refresh the list
      } else {
        toast.error(data.error || 'Failed to delete report');
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Failed to delete report');
    }
  };

  // Memoize filters to prevent unnecessary re-renders
  // Use JSON.stringify for array comparison to ensure changes trigger updates
  const memoizedFilters = useMemo(() => {
    // Don't create filters until we've checked for saved reports
    // This prevents child components from loading default data before saved reports are checked
    if (!hasAutoLoadedReport) {
      console.log('⏳ Waiting for saved reports check before creating filters... (hasAutoLoadedReport:', hasAutoLoadedReport, ')');
      return null;
    }
    
    console.log('✅ Creating filters now! hasAutoLoadedReport:', hasAutoLoadedReport, 'dataLoaded:', dataLoaded);
    
    const filters = { 
      dateRange: selectedRange,
      dateRangeType: dateRangeType,
      startDate: startDate,
      endDate: endDate,
      adNameFilter: adNameFilter.keywords.filter(k => k.trim()).join(' '),
      adNameFilterKeywords: adNameFilter.keywords.filter(k => k.trim()),
      adNameFilterLogic: adNameFilter.logic,
      adNameFilterMode: adNameFilter.mode,
      correlationMetric: correlationMetric,
      refreshTrigger: refreshTrigger // Force refresh when this changes
    };
    console.log('🔄 Memoized filters updated:', {
      dateRange: filters.dateRange,
      dateRangeType: filters.dateRangeType,
      startDate: filters.startDate,
      endDate: filters.endDate,
      keywords: filters.adNameFilterKeywords,
      logic: filters.adNameFilterLogic,
      mode: filters.adNameFilterMode,
      correlationMetric: filters.correlationMetric,
      refreshTrigger: filters.refreshTrigger
    });
    return filters;
  }, [
    hasAutoLoadedReport, // Wait for saved reports check
    selectedRange,
    dateRangeType,
    startDate,
    endDate,
    JSON.stringify(adNameFilter), // Stringify entire object for deep comparison
    correlationMetric,
    refreshTrigger
  ]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100 pb-8">
      {/* Loading Overlay */}
      {/* Removed full-screen loading overlay */}

      {/* Header */}
      <div className="border-b border-gray-200 bg-white/90 backdrop-blur-sm shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between p-4 space-y-4 lg:space-y-0">
          {/* Left: Logo + Report Buttons */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <img 
                src="/sslogo.png" 
                alt="ShowStop Logo" 
                className="h-12 w-auto"
              />
            </div>
            
            {/* Report Buttons - Show for dashboard tabs with filtering */}
            {(activeDashboardTab === 'top-performing' || activeDashboardTab === 'ad-type-analysis' || activeDashboardTab === 'copy-theme-analysis' || activeDashboardTab === 'creative-attributes' || activeDashboardTab === 'creative-breakdown' || activeDashboardTab === 'media-library' ||
              activeDashboardTab === 'asset-id' || activeDashboardTab === 'campaign-name' || 
              activeDashboardTab === 'value-proposition' || activeDashboardTab === 'content-category' || 
              activeDashboardTab === 'talent-quantity' || activeDashboardTab === 'talent-gender' || activeDashboardTab === 'talent-age' ||
              activeDashboardTab === 'setting' || activeDashboardTab === 'music-playing' || activeDashboardTab === 'music-genre' ||
              activeDashboardTab === 'voiceover' || activeDashboardTab === 'dominant-color' || activeDashboardTab === 'video-length') && (
              <div className="flex items-center space-x-2">
                {/* Create Report Button */}
                <button
                  onClick={() => {
                    setShowDatePicker(!showDatePicker);
                    if (!showDatePicker) {
                      setActiveTab('dateRange'); // Reset to date range tab when opening
                      // Initialize pending filter with current filter values, ensuring at least one row
                      const initialKeywords = adNameFilter.keywords.length > 0 ? adNameFilter.keywords : [''];
                      setPendingAdNameFilter({ 
                        ...adNameFilter, 
                        keywords: initialKeywords,
                        mode: adNameFilter.mode || 'include' // Ensure mode is set
                      });
                    }
                  }}
                  disabled={isLoading || !dataLoaded}
                  className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isLoading || !dataLoaded
                      ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                  title="Create Report"
                >
                  <Plus className="h-4 w-4" />
                  <span>Create Report</span>
                </button>
                
                {/* Saved Reports Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowSavedReportsList(!showSavedReportsList)}
                    className="flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors bg-indigo-600 text-white hover:bg-indigo-700"
                    title="Saved Reports"
                  >
                    <FileText className="h-4 w-4" />
                    <span>Reports</span>
                    {savedReports.length > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 bg-white text-indigo-600 rounded-full text-xs font-bold">
                        {savedReports.length}
                      </span>
                    )}
                  </button>
                  
                  {/* Saved Reports Dropdown Menu */}
                  {showSavedReportsList && createPortal(
                    <div 
                      className="fixed inset-0 z-[9998]"
                      onClick={() => setShowSavedReportsList(false)}
                    >
                      <div 
                        className="absolute left-4 top-20 bg-white rounded-lg shadow-xl border border-gray-200 w-80 max-h-96 overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="p-3 border-b border-gray-200 flex items-center justify-between">
                          <h4 className="font-semibold text-gray-900">Saved Reports</h4>
                          <button
                            onClick={() => setShowSavedReportsList(false)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        
                        {savedReports.length > 0 ? (
                          <div className="divide-y divide-gray-100">
                            {savedReports.map((report) => (
                              <div
                                key={report.id}
                                className="p-3 hover:bg-gray-50 group"
                              >
                                <div className="flex items-start justify-between">
                                  <button
                                    onClick={() => handleLoadReport(report.id)}
                                    disabled={loadingReportId === report.id}
                                    className={`flex-1 text-left ${loadingReportId === report.id ? 'opacity-50 cursor-wait' : 'hover:opacity-75'}`}
                                  >
                                    <div className="flex items-center gap-2 mb-1">
                                      <h5 className="font-medium text-gray-900 text-sm">
                                        {report.report_name}
                                      </h5>
                                    </div>
                                    <div className="text-xs text-gray-500 space-y-0.5">
                                      {/* Report Type Display */}
                                      <div className="flex items-center gap-1">
                                        <span>📋</span>
                                        <span>{REPORT_TYPE_NAMES[report.report_type || report.dashboard_type] || (report.report_type || report.dashboard_type)}</span>
                                      </div>
                                      
                                      {/* Date Range Display */}
                                      <div className="flex items-center gap-1">
                                        <span>📅</span>
                                        {report.start_date && report.end_date ? (
                                          (() => {
                                            // Extract date part from ISO strings (handles both '2025-11-01' and '2025-11-01T07:00:00.000Z')
                                            const startDateStr = typeof report.start_date === 'string' 
                                              ? report.start_date.split('T')[0]  // Get YYYY-MM-DD part
                                              : report.start_date.toISOString().split('T')[0];
                                            const endDateStr = typeof report.end_date === 'string'
                                              ? report.end_date.split('T')[0]  // Get YYYY-MM-DD part
                                              : report.end_date.toISOString().split('T')[0];
                                            
                                            // Parse with local timezone
                                            const startDate = new Date(startDateStr + 'T00:00:00');
                                            const endDate = new Date(endDateStr + 'T00:00:00');
                                            
                                            return (
                                              <span>
                                                {startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                              </span>
                                            );
                                          })()
                                        ) : (
                                          <span>{report.date_range_days} days</span>
                                        )}
                                      </div>
                                      
                                      {/* Correlation Metric */}
                                      <div>📊 {report.correlation_metric?.toUpperCase() || 'ROAS'}</div>
                                      
                                      {/* Creation Time */}
                                      <div className="text-gray-400">
                                        🕒 Created {new Date(report.created_at).toLocaleDateString('en-US', { 
                                          month: 'short', 
                                          day: 'numeric', 
                                          year: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </div>
                                      
                                      {/* Keywords with Logic and Mode */}
                                      {report.ad_name_filter?.keywords?.length > 0 && (
                                        <div className="flex items-center gap-1 flex-wrap">
                                          <span className={report.ad_name_filter.mode === 'exclude' ? 'text-red-500' : 'text-green-600'}>
                                            {report.ad_name_filter.mode === 'exclude' ? '❌' : '✅'}
                                          </span>
                                          <span className="text-xs font-medium text-gray-600">
                                            {report.ad_name_filter.mode === 'exclude' ? 'Exclude' : 'Include'}:
                                          </span>
                                          {report.ad_name_filter.keywords.map((keyword, idx) => (
                                            <span key={idx} className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                              report.ad_name_filter.mode === 'exclude' 
                                                ? 'bg-red-50 text-red-700' 
                                                : 'bg-blue-50 text-blue-700'
                                            }`}>
                                              {keyword}
                                            </span>
                                          ))}
                                          {report.ad_name_filter.keywords.length > 1 && (
                                            <span className="text-xs font-medium text-gray-500">
                                              ({report.ad_name_filter.logic || 'AND'})
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteReport(report.id, report.report_name)}
                                    className="ml-2 p-1 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Delete report"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="p-6 text-center text-gray-500 text-sm">
                            <FileText className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                            <p>No saved reports yet</p>
                            <p className="text-xs mt-1">Configure filters and click "Save Report"</p>
                          </div>
                        )}
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
                
                {/* Save Report Button */}
                <button
                  onClick={() => {
                    // Auto-populate report name based on current configuration
                    const suggestedName = generateReportName();
                    setNewReportName(suggestedName);
                    setShowSaveReportModal(true);
                  }}
                  className="flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors bg-green-600 text-white hover:bg-green-700"
                  title="Save current filter configuration"
                  disabled={isLoading || !dataLoaded}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                  <span>Save Report</span>
                </button>
              </div>
            )}
          </div>
          
          {/* Center: Loading Message */}
          <div className="flex-1 flex justify-center">
            {Object.values(sectionLoading).some(loading => loading) && (
              <div className="px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex items-center justify-center space-x-2 text-blue-600 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{Math.round(dashboardLoadingProgress)}%</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Right: Action Buttons & Controls */}
          <div className="flex items-center justify-end space-x-3">
            {/* Date Range & Filter Display - Show for dashboard tabs with filtering */}
            {(activeDashboardTab === 'top-performing' || activeDashboardTab === 'ad-type-analysis' || activeDashboardTab === 'copy-theme-analysis' || activeDashboardTab === 'creative-attributes' || activeDashboardTab === 'creative-breakdown' || activeDashboardTab === 'media-library' ||
              activeDashboardTab === 'asset-id' || activeDashboardTab === 'campaign-name' || activeDashboardTab === 'concept' || 
              activeDashboardTab === 'value-proposition' || activeDashboardTab === 'content-category' || 
              activeDashboardTab === 'talent-quantity' || activeDashboardTab === 'talent-gender' || activeDashboardTab === 'talent-age' ||
              activeDashboardTab === 'setting' || activeDashboardTab === 'music-playing' || activeDashboardTab === 'music-genre' ||
              activeDashboardTab === 'voiceover' || activeDashboardTab === 'dominant-color' || activeDashboardTab === 'video-length') && (
              <div className="flex items-center space-x-2 text-sm text-gray-600 mr-4">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-gray-600">
                  {formatDateRange()}
                </span>
                
                {/* Ad Name Filter Display */}
                {adNameFilter.keywords.length > 0 && adNameFilter.keywords.some(k => k.trim()) && (
                  <div className="hidden md:flex items-center space-x-2 text-gray-600">
                    <span className="text-xs">•</span>
                    <span className="text-xs">Keywords:</span>
                    <div className="flex flex-wrap gap-1">
                      {adNameFilter.keywords.filter(k => k.trim()).map((keyword, index) => (
                        <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Media Library Button */}
            <button
              onClick={() => handleDashboardTabChange('media-library')}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 transform hover:scale-105 ${
                activeDashboardTab === 'media-library' 
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg' 
                  : 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white'
              }`}
              title="Media Library - Edit Creative Labels"
            >
              <FileText className="h-4 w-4" />
              <span className="font-medium text-sm">Media Library</span>
            </button>
            
            {/* Trend Analytics Button - Only show if data exists */}
            {hasTrendAnalyticsData && (
              <button
                onClick={() => navigate('/trend-analytics')}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 transform hover:scale-105 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                title="Trend Analytics - Analyze Hook, Messaging, and Concept Patterns"
              >
                <TrendingUp className="h-4 w-4" />
                <span className="font-medium text-sm">Trend Analytics</span>
              </button>
            )}
            
            {/* Trend Spotter Button */}
            <button
              onClick={() => navigate('/trend-spotter')}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 transform hover:scale-105 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white"
              title="Trend Spotter - Discover Trends from TikTok/Instagram URLs"
            >
              <Sparkles className="h-4 w-4" />
              <span className="font-medium text-sm">Trend Spotter</span>
            </button>
            
            {/* Custom Labeling Button */}
            <button
              onClick={() => navigate('/custom-labeling')}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 transform hover:scale-105 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
              title="Custom Labeling - Create and Manage Custom Brand Labels"
            >
              <Tag className="h-4 w-4" />
              <span className="font-medium text-sm">Custom Labels</span>
            </button>
            
            {/* Logout Button */}
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all duration-200 transform hover:scale-105"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
            

          </div>
        </div>

        {/* Enhanced Date Range Picker with Tabs */}
        {showDatePicker && createPortal(
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
            <div className="bg-slate-800 rounded-lg p-4 border border-slate-700 shadow-xl mx-auto max-h-[90vh] overflow-y-auto w-full max-w-4xl">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-white">Report Configuration</h4>
                <button
                  onClick={() => setShowDatePicker(false)}
                  className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-700 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* Tab Navigation */}
              <div className="flex space-x-1 mb-6 border-b border-slate-700">
                <button
                  onClick={() => setActiveTab('dateRange')}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    activeTab === 'dateRange'
                      ? 'bg-slate-700 text-white border-b-2 border-purple-500'
                      : 'text-slate-400 hover:text-slate-300 hover:bg-slate-700/50'
                  }`}
                >
                  Date Range
                </button>
                <button
                  onClick={() => setActiveTab('adFilter')}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    activeTab === 'adFilter'
                      ? 'bg-slate-700 text-white border-b-2 border-purple-500'
                      : 'text-slate-400 hover:text-slate-300 hover:bg-slate-700/50'
                  }`}
                >
                  Ad Name Filter
                </button>
                <button
                  onClick={() => setActiveTab('correlation')}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    activeTab === 'correlation'
                      ? 'bg-slate-700 text-white border-b-2 border-purple-500'
                      : 'text-slate-400 hover:text-slate-300 hover:bg-slate-700/50'
                  }`}
                >
                  Correlation Settings
                </button>
                <button
                  onClick={() => setActiveTab('reportType')}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    activeTab === 'reportType'
                      ? 'bg-slate-700 text-white border-b-2 border-purple-500'
                      : 'text-slate-400 hover:text-slate-300 hover:bg-slate-700/50'
                  }`}
                >
                  Group By
                </button>
              </div>
              
              {/* Date Range Tab Content */}
              {activeTab === 'dateRange' && (
                <div>
                  {/* Preset Ranges */}
                  <div className="mb-6">
                    <h5 className="text-sm font-medium text-slate-300 mb-3">Quick Select</h5>
                    <p className="text-xs text-slate-400 mb-3">Click any preset range to instantly apply it</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {getPresetRanges().map((preset) => (
                        <button
                          key={preset.days}
                          onClick={() => handlePresetRange(preset.days)}
                          className={`p-2 rounded-lg text-xs font-medium transition-all duration-200 transform hover:scale-105 ${
                            selectedRange === preset.days && dateRangeType === 'preset'
                              ? `bg-gradient-to-r ${preset.color} text-white shadow-lg`
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600 border border-slate-600'
                          }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>

              {/* Custom Date Range */}
              <div className="mb-6">
                <h5 className="text-sm font-medium text-slate-300 mb-3">Custom Range</h5>
                <p className="text-xs text-slate-400 mb-3">Select specific start and end dates (max 365 days)</p>
                <div className="grid grid-cols-1 gap-3 mb-4">
                  <div>
                    <label className="block text-xs text-slate-400 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-2">End Date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                    />
                  </div>
                </div>
                <button
                  onClick={handleCustomDateRangeSubmit}
                  disabled={!startDate || !endDate}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 transform hover:scale-105"
                >
                  Apply Custom Range
                </button>
              </div>



                  {/* Legacy Days Input */}
                  <div className="border-t border-slate-700 pt-4">
                    <h5 className="text-sm font-medium text-slate-300 mb-3">Or Enter Days</h5>
                    <div className="flex items-center space-x-3">
                      <input
                        type="number"
                        value={customDays || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setCustomDays('');
                          } else {
                            const numValue = parseInt(value);
                            if (!isNaN(numValue) && numValue >= 1 && numValue <= 365) {
                              setCustomDays(numValue);
                            }
                          }
                        }}
                        min="1"
                        max="365"
                        className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-purple-500"
                        placeholder="Enter days (1-365)"
                      />
                      <button
                        onClick={handleCustomDaysSubmit}
                        className="bg-slate-600 hover:bg-slate-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        Apply
                      </button>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Enter a number between 1 and 365 days</p>
                  </div>
                </div>
              )}

                            {/* Ad Name Filter Tab Content */}
              {activeTab === 'adFilter' && (
                <div>
                  {/* Filter Mode Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-300 mb-2">Filter Mode</label>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePendingAdNameFilterChange({ ...pendingAdNameFilter, mode: 'include' })}
                        className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                          pendingAdNameFilter.mode === 'include'
                            ? 'bg-green-500 text-white'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        ✅ Include (show ads with keywords)
                      </button>
                      <button
                        onClick={() => handlePendingAdNameFilterChange({ ...pendingAdNameFilter, mode: 'exclude' })}
                        className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                          pendingAdNameFilter.mode === 'exclude'
                            ? 'bg-red-500 text-white'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        ❌ Exclude (hide ads with keywords)
                      </button>
                    </div>
                  </div>

                  {/* Logic Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-300 mb-2">Match Logic</label>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePendingAdNameFilterChange({ ...pendingAdNameFilter, logic: 'OR' })}
                        className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                          pendingAdNameFilter.logic === 'OR'
                            ? 'bg-purple-500 text-white'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        OR (any keyword)
                      </button>
                      <button
                        onClick={() => handlePendingAdNameFilterChange({ ...pendingAdNameFilter, logic: 'AND' })}
                        className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                          pendingAdNameFilter.logic === 'AND'
                            ? 'bg-purple-500 text-white'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        AND (all keywords)
                      </button>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      {pendingAdNameFilter.mode === 'include' 
                        ? `Show ads that ${pendingAdNameFilter.logic === 'OR' ? 'contain ANY of' : 'contain ALL of'} the keywords`
                        : `Hide ads that ${pendingAdNameFilter.logic === 'OR' ? 'contain ANY of' : 'contain ALL of'} the keywords`
                      }
                    </div>
                  </div>
                  
                  {/* Keywords Input */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-slate-300">Keywords</label>
                      <div className="flex space-x-1">
                        <button
                          type="button"
                          onClick={() => {
                            const newKeywords = [...pendingAdNameFilter.keywords, ''];
                            handlePendingAdNameFilterChange({ ...pendingAdNameFilter, keywords: newKeywords });
                          }}
                          className="w-6 h-6 rounded-full bg-green-500 hover:bg-green-600 text-white text-sm font-bold flex items-center justify-center transition-colors"
                          title="Add keyword row"
                        >
                          +
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (pendingAdNameFilter.keywords.length > 1) {
                              const newKeywords = pendingAdNameFilter.keywords.slice(0, -1);
                              handlePendingAdNameFilterChange({ ...pendingAdNameFilter, keywords: newKeywords });
                            }
                          }}
                          disabled={pendingAdNameFilter.keywords.length <= 1}
                          className={`w-6 h-6 rounded-full text-white text-sm font-bold flex items-center justify-center transition-colors ${
                            pendingAdNameFilter.keywords.length <= 1
                              ? 'bg-slate-600 cursor-not-allowed'
                              : 'bg-red-500 hover:bg-red-600'
                          }`}
                          title="Remove keyword row"
                        >
                          -
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {pendingAdNameFilter.keywords.map((keyword, index) => (
                        <input
                          key={index}
                          type="text"
                          placeholder={`Keyword ${index + 1}`}
                          value={keyword}
                          onChange={(e) => {
                            const newKeywords = [...pendingAdNameFilter.keywords];
                            newKeywords[index] = e.target.value;
                            handlePendingAdNameFilterChange({ ...pendingAdNameFilter, keywords: newKeywords });
                          }}
                          className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Apply Button */}
                  <button
                    onClick={applyAdNameFilter}
                    disabled={JSON.stringify(pendingAdNameFilter) === JSON.stringify(adNameFilter)}
                    className={`w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      JSON.stringify(pendingAdNameFilter) === JSON.stringify(adNameFilter)
                        ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                        : 'bg-purple-500 hover:bg-purple-600 text-white'
                    }`}
                  >
                    {JSON.stringify(pendingAdNameFilter) === JSON.stringify(adNameFilter)
                      ? 'No Changes'
                      : 'Apply Filter'
                    }
                  </button>
                </div>
              )}

              {/* Correlation Settings Tab Content */}
              {activeTab === 'correlation' && (
                <div>
                  <div className="mb-6">
                    <h5 className="text-sm font-medium text-slate-300 mb-3">Correlation Metric</h5>
                    <p className="text-xs text-slate-400 mb-4">Choose which performance metric to correlate with all analytics tabs</p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { value: 'roas', label: 'ROAS', description: 'Return on Ad Spend', icon: '📈' },
                        { value: 'revenue', label: 'Revenue', description: 'Total Revenue', icon: '💰' },
                        { value: 'spend', label: 'Spend', description: 'Total Spend', icon: '💸' },
                        { value: 'ctr', label: 'CTR', description: 'Click-Through Rate', icon: '🎯' },
                        { value: 'cpa', label: 'CPA', description: 'Cost Per Acquisition', icon: '💵' }
                      ].map((metric) => (
                        <button
                          key={metric.value}
                          onClick={() => setCorrelationMetric(metric.value)}
                          className={`p-4 rounded-lg border transition-all ${
                            correlationMetric === metric.value
                              ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                              : 'border-slate-600 hover:border-slate-500 text-slate-400 hover:text-slate-300'
                          }`}
                        >
                          <div className="text-2xl mb-2">{metric.icon}</div>
                          <div className="text-sm font-medium">{metric.label}</div>
                          <div className="text-xs text-slate-400 mt-1">{metric.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Group By Tab Content */}
              {activeTab === 'reportType' && (
                <div>
                  <div className="mb-6">
                    <h5 className="text-sm font-medium text-slate-300 mb-3">Group By</h5>
                    <p className="text-xs text-slate-400 mb-4">Select how to group and analyze your ads</p>
                    
                    {/* Two-column grid layout */}
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'copy-theme-analysis', label: 'Hook', description: 'AI-powered hook pattern analysis', icon: '🎯' },
                        { id: 'ad-type-analysis', label: 'Ad Type', description: 'Performance by ad format and type', icon: '📱' },
                        { id: 'top-performing', label: 'Ad Name', description: 'Best performing ads with assets', icon: '📝' },
                        { id: 'asset-id', label: 'Asset ID', description: 'Group by unique asset identifier', icon: '🆔' },
                        { id: 'campaign-name', label: 'Campaign Name', description: 'Performance grouped by campaign', icon: '📋' },
                        { id: 'value-proposition', label: 'Value Proposition', description: 'Sales angles and product benefits', icon: '💎' },
                        { id: 'content-category', label: 'Content Category', description: 'Type of content or product category', icon: '📂' },
                        { id: 'talent-quantity', label: 'Number of Talent', description: 'Count of people in the ad', icon: '🔢' },
                        { id: 'talent-gender', label: 'Male vs Female', description: 'Gender representation in ads', icon: '⚧️' },
                        { id: 'talent-age', label: 'Age Range', description: 'Age demographics of talent', icon: '🎂' },
                        { id: 'setting', label: 'Setting', description: 'Environment or location in the ad', icon: '📍' },
                        { id: 'music-playing', label: 'Music Playing (Y/N)', description: 'Whether music is present', icon: '🎵' },
                        { id: 'music-genre', label: 'Music Genre', description: 'Type of music used in ads', icon: '🎶' },
                        { id: 'voiceover', label: 'Voiceover (Y/N)', description: 'Whether voiceover narration is used', icon: '🎤' },
                        { id: 'dominant-color', label: 'Dominant Color', description: 'Primary color scheme of the ad', icon: '🎨' },
                        { id: 'video-length', label: 'Video Length', description: 'Duration ranges (0-14s, 15-29s, etc.)', icon: '⏱️' }
                      ].map((option) => {
                        const isActive = 
                          (option.id === 'top-performing' && activeDashboardTab === 'top-performing') ||
                          (option.id === 'copy-theme-analysis' && activeDashboardTab === 'copy-theme-analysis') ||
                          (option.id === 'ad-type-analysis' && activeDashboardTab === 'ad-type-analysis') ||
                          (option.id !== 'top-performing' && option.id !== 'copy-theme-analysis' && option.id !== 'ad-type-analysis' && activeDashboardTab === option.id);
                        
                        return (
                          <button
                            key={option.id}
                            onClick={() => {
                              if (option.id === 'top-performing') {
                                handleDashboardTabChange('top-performing');
                              } else if (option.id === 'copy-theme-analysis') {
                                handleDashboardTabChange('copy-theme-analysis');
                          } else if (option.id === 'ad-type-analysis') {
                            handleDashboardTabChange('ad-type-analysis');
                          } else {
                            handleDashboardTabChange(option.id);
                          }
                              setShowDatePicker(false);
                            }}
                            className={`p-3 rounded-lg border transition-all text-left ${
                              isActive
                                ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                                : 'border-slate-600 hover:border-slate-500 text-slate-400 hover:text-slate-300'
                            }`}
                          >
                            <div className="flex items-start space-x-2">
                              <span className="text-lg flex-shrink-0">{option.icon}</span>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium">{option.label}</div>
                                <div className="text-xs text-slate-400 mt-0.5 leading-tight">{option.description}</div>
                              </div>
                              {isActive && (
                                <Check className="h-4 w-4 text-purple-400 flex-shrink-0" />
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
      </div>

      <div className="flex h-[calc(100vh-80px)]">
        {/* Main Dashboard */}
        <div className={`flex-1 transition-all duration-300 ${
          // On mobile (sm and below), chat overlays instead of pushing content
          chatSize === 'large' ? 'mr-[50%] xl:mr-[50%] lg:mr-[60%] md:mr-[70%] sm:mr-0' : 
          chatSize === 'small' ? 'mr-96 xl:mr-96 lg:mr-80 md:mr-64 sm:mr-0' : 
          chatSize === 'custom' ? '' :
          'mr-20 sm:mr-0'
        } p-6 xl:p-6 lg:p-4 md:p-3 sm:p-2 overflow-y-auto`} style={chatSize === 'custom' ? { marginRight: `${customChatWidth}px` } : {}}>
          
          
          {/* Tab Content - Show different dashboards based on selected tab */}
          {(activeDashboardTab === 'top-performing' || activeDashboardTab === 'ad-type-analysis' || activeDashboardTab === 'copy-theme-analysis' || activeDashboardTab === 'creative-attributes' || activeDashboardTab === 'creative-breakdown' || activeDashboardTab === 'media-library' ||
            activeDashboardTab === 'asset-id' || activeDashboardTab === 'campaign-name' || activeDashboardTab === 'concept' || 
            activeDashboardTab === 'value-proposition' || activeDashboardTab === 'content-category' || 
            activeDashboardTab === 'talent-quantity' || activeDashboardTab === 'talent-gender' || activeDashboardTab === 'talent-age' ||
            activeDashboardTab === 'setting' || activeDashboardTab === 'music-playing' || activeDashboardTab === 'music-genre' ||
            activeDashboardTab === 'voiceover' || activeDashboardTab === 'dominant-color' || activeDashboardTab === 'video-length') && (
            <div className="mb-8">
              <MainDashboard 
                activeTab={activeDashboardTab}
                dashboardData={dashboardData} 
                filters={memoizedFilters} 
                onFilterChange={(newFilters) => {
                  if (newFilters.type === 'chat') {
                    // Handle chat message from TopPerformingAds or AdTypeAnalysis components
                    // Only replace input if it's empty or just whitespace
                    if (!inputMessage.trim()) {
                      setInputMessage(newFilters.message);
                    } else {
                      // If user has typed something, append the message
                      setInputMessage(prev => prev + (prev.endsWith(' ') ? '' : ' ') + newFilters.message);
                    }
                    setChatSize('large'); // Open chat in large size
                  } else if (newFilters.dateRange) {
                    setSelectedRange(newFilters.dateRange);
                  } else if (newFilters.adNameFilter) {
                    setAdNameFilter({ keywords: [newFilters.adNameFilter] });
                  }
                }}
              />
            </div>
          )}
          
          {/* Legacy Tab Content - Show other dashboards */}
          {(activeDashboardTab !== 'top-performing' && activeDashboardTab !== 'ad-type-analysis' && activeDashboardTab !== 'copy-theme-analysis' && activeDashboardTab !== 'creative-attributes' && activeDashboardTab !== 'creative-breakdown' && activeDashboardTab !== 'media-library' &&
            activeDashboardTab !== 'asset-id' && activeDashboardTab !== 'campaign-name' && activeDashboardTab !== 'concept' && 
            activeDashboardTab !== 'value-proposition' && activeDashboardTab !== 'content-category' && 
            activeDashboardTab !== 'talent-quantity' && activeDashboardTab !== 'talent-gender' && activeDashboardTab !== 'talent-age' &&
            activeDashboardTab !== 'setting' && activeDashboardTab !== 'music-playing' && activeDashboardTab !== 'music-genre' &&
            activeDashboardTab !== 'voiceover' && activeDashboardTab !== 'dominant-color' && activeDashboardTab !== 'video-length') && (
            <div className="mb-8">
              {activeDashboardTab === 'audience-creative' && (
                <AudienceCreative 
                  dashboardData={dashboardData} 
                  filters={{ dateRange: { startDate, endDate, selectedRange }, adNameFilter }} 
                  onFilterChange={(newFilters) => {
                    if (newFilters.dateRange) {
                      setStartDate(newFilters.dateRange.startDate);
                      setEndDate(newFilters.dateRange.endDate);
                      setSelectedRange(newFilters.dateRange.selectedRange);
                    }
                    if (newFilters.adNameFilter) {
                      setAdNameFilter(newFilters.adNameFilter);
                    }
                  }} 
                />
              )}
              {activeDashboardTab === 'time-trends' && (
                <TimeTrends 
                  dashboardData={dashboardData} 
                  filters={{ dateRange: { startDate, endDate, selectedRange }, adNameFilter }} 
                  onFilterChange={(newFilters) => {
                    if (newFilters.type === 'chat') {
                      // Handle chat message from TimeTrends component
                      // Only replace input if it's empty or just whitespace
                      if (!inputMessage.trim()) {
                        setInputMessage(newFilters.message);
                      } else {
                        // If user has typed something, append the message
                        setInputMessage(prev => prev + (prev.endsWith(' ') ? '' : ' ') + newFilters.message);
                      }
                      setChatSize('large'); // Open chat in large size
                    } else if (newFilters.dateRange) {
                      setStartDate(newFilters.dateRange.startDate);
                      setEndDate(newFilters.dateRange.endDate);
                      setSelectedRange(newFilters.dateRange.selectedRange);
                    } else if (newFilters.adNameFilter) {
                      setAdNameFilter(newFilters.adNameFilter);
                    }
                  }}
                  accountId={getAccountId()}
                />
              )}
              {activeDashboardTab === 'platform-placement' && (
                <PlatformPlacement 
                  dashboardData={dashboardData} 
                  filters={{ dateRange: { startDate, endDate, selectedRange }, adNameFilter }} 
                  onFilterChange={(newFilters) => {
                    if (newFilters.dateRange) {
                      setStartDate(newFilters.dateRange.startDate);
                      setEndDate(newFilters.dateRange.endDate);
                      setSelectedRange(newFilters.dateRange.selectedRange);
                    }
                    if (newFilters.adNameFilter) {
                      setAdNameFilter(newFilters.adNameFilter);
                    }
                  }} 
                />
              )}

            </div>
          )}
          
          {/* Overview Dashboard Content - Only show when legacy tabs are selected (hidden for now) */}
          {false && (
            <>
              {/* Quick Stats */}
              <div className={`grid gap-6 mb-8 ${
                getEnabledMetrics().length === 1 ? 'grid-cols-1' :
                getEnabledMetrics().length === 2 ? 'grid-cols-1 md:grid-cols-2' :
                getEnabledMetrics().length === 3 ? 'grid-cols-1 md:grid-cols-3' :
                'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
              }`} data-chart-id="key_metrics">
            {sectionLoading.metrics ? (
              // Loading skeleton for metrics
              Array.from({ length: Math.max(4, getEnabledMetrics().length) }).map((_, index) => (
                <div key={index} className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 rounded-lg bg-slate-700 animate-pulse">
                      <div className="h-6 w-6 bg-slate-600 rounded"></div>
                    </div>
                    <div className="h-4 w-12 bg-slate-700 rounded animate-pulse"></div>
                  </div>
                  <div className="space-y-1 mb-8">
                    <div className="h-8 w-20 bg-slate-700 rounded animate-pulse"></div>
                    <div className="h-4 w-24 bg-slate-700 rounded animate-pulse"></div>
                  </div>
                </div>
              ))
            ) : (
              (() => {
                const enabledMetrics = getEnabledMetrics();
                console.log('Rendering metrics with enabled metrics:', enabledMetrics);
                console.log('Current dashboardData.metrics:', dashboardData.metrics);
                return enabledMetrics.map((metricKey, index) => {
                  const metricConfig = {
                  totalImpressions: { label: 'Total Impressions', icon: Activity, color: 'from-purple-500 to-pink-500', metric: 'total_impressions' },
                  totalSpend: { label: 'Total Spend', icon: DollarSign, color: 'from-cyan-500 to-blue-500', metric: 'total_spend' },
                  totalClicks: { label: 'Total Clicks', icon: Target, color: 'from-green-500 to-emerald-500', metric: 'total_clicks' },
                  totalConversions: { label: 'Conversions', icon: TrendingUp, color: 'from-orange-500 to-red-500', metric: 'total_conversions' },
                  totalRevenue: { label: 'Total Revenue', icon: DollarSign, color: 'from-green-500 to-emerald-500', metric: 'total_revenue' },
                  avgCTR: { label: 'Average CTR', icon: Target, color: 'from-blue-500 to-cyan-500', metric: 'avg_ctr' },
                  avgCPC: { label: 'Average CPC', icon: DollarSign, color: 'from-yellow-500 to-orange-500', metric: 'avg_cpc' },
                  avgROAS: { label: 'Average ROAS', icon: TrendingUp, color: 'from-green-500 to-emerald-500', metric: 'avg_roas' },
                  avgCPA: { label: 'Average CPA', icon: DollarSign, color: 'from-red-500 to-pink-500', metric: 'avg_cpa' },
                  avgCVR: { label: 'Average CVR', icon: Target, color: 'from-purple-500 to-indigo-500', metric: 'avg_cvr' }
                };
                
                const config = metricConfig[metricKey];
                const value = dashboardData.metrics[metricKey] || '0';
                let growthKey;
                if (metricKey.startsWith('total')) {
                  growthKey = metricKey.replace('total', '') + 'Growth';
                } else if (metricKey.startsWith('avg')) {
                  growthKey = metricKey + 'Growth';
                } else {
                  growthKey = metricKey + 'Growth';
                }
                const change = dashboardData.metrics[growthKey] || '+0%';
                

                
                                 return (
                   <div key={index} className="group transform hover:scale-105 transition-all duration-200">
                     <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6 hover:border-purple-500/50 transition-all duration-200 relative">
                       <div className="flex items-center justify-between mb-4">
                         <div className={`p-3 rounded-lg bg-gradient-to-r ${config.color} opacity-80 group-hover:opacity-100 transition-opacity`}>
                           <config.icon className="h-6 w-6 text-white" />
                         </div>
                         <span className="text-green-400 text-sm font-medium">{change}</span>
                       </div>
                       <div className="space-y-1 mb-8">
                         <p className="text-2xl font-bold text-white">{value}</p>
                         <p className="text-slate-400 text-sm">{config.label}</p>
                       </div>
                       
                       {/* AI Chat Button */}
                       <button
                         onClick={() => handleMetricClick(config.metric, { label: config.label, value, change })}
                         className="absolute bottom-4 right-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white p-2 rounded-lg transition-all duration-200 transform hover:scale-110 opacity-0 group-hover:opacity-100"
                         title="Analyze with AI"
                       >
                         <MessageCircle className="h-4 w-4" />
                       </button>
                     </div>
                   </div>
                 );
               });
             })()
            )}
          </div>

          {/* Performance Trends - Full Width */}
          <div className="mb-8" data-chart-id="performance_trends">
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6 hover:border-purple-500/30 transition-all duration-200">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-white flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-purple-400" />
                  Performance Trends ({selectedRange > 30 ? 'Weekly' : 'Daily'} - {formatDateRange()})
                </h3>
                <button 
                  onClick={() => handleMetricClick('performance_trends', dashboardData.performanceData)}
                  className="text-purple-400 hover:text-purple-300 transition-colors"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
              {sectionLoading.trends ? (
                <Loader2 className="mx-auto my-8 animate-spin text-purple-400" />
              ) : !dashboardData.performanceData || dashboardData.performanceData.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <div className="mb-4">
                    <BarChart3 className="h-12 w-12 mx-auto text-slate-600" />
                  </div>
                  <h4 className="text-lg font-medium text-slate-300 mb-2">No Performance Trends Data Available</h4>
                  <p className="text-sm text-slate-500 mb-4">
                    The current filters returned no results. Try adjusting your date range or ad name filters.
                  </p>
                  <div className="flex items-center justify-center space-x-2 text-xs text-slate-500">
                    <span>💡 Tip:</span>
                    <span>Use broader date ranges or remove ad name filters to see more data</span>
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={dashboardData.performanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="name" 
                      stroke="#9ca3af" 
                      fontSize={11}
                      angle={dashboardData.performanceData?.some(item => item.name && item.name.toString().length > 8) ? -45 : 0}
                      textAnchor={dashboardData.performanceData?.some(item => item.name && item.name.toString().length > 8) ? "end" : "middle"}
                      height={dashboardData.performanceData?.some(item => item.name && item.name.toString().length > 8) ? 80 : 60}
                      interval={0}
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis yAxisId="left" stroke="#06b6d4" />
                    <YAxis yAxisId="right" orientation="right" stroke="#8b5cf6" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1e293b', 
                        border: '1px solid #475569',
                        borderRadius: '8px'
                      }} 
                    />
                    <Line yAxisId="left" type="monotone" dataKey="spend" stroke="#06b6d4" strokeWidth={3} name="Spend" />
                    <Line yAxisId="right" type="monotone" dataKey="roas" stroke="#8b5cf6" strokeWidth={3} name="ROAS" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Ad Types Breakdown */}
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6" data-chart-id="ad_types">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Ad Types Performance</h3>
              <button
                onClick={() => setShowAdTypeConfig(true)}
                disabled={sectionLoading.adTypes}
                className={`p-2 rounded-lg transition-colors ${
                  sectionLoading.adTypes 
                    ? 'text-slate-600 cursor-not-allowed' 
                    : 'text-slate-400 hover:text-purple-400 hover:bg-slate-700/50'
                }`}
                title={sectionLoading.adTypes ? "Loading..." : "Configure table view"}
              >
                <Settings className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {sectionLoading.adTypes ? (
                <Loader2 className="mx-auto my-8 animate-spin text-purple-400" />
              ) : (
                (() => {
                  // Sort and filter data based on configuration
                  let processedData = [...(dashboardData.adTypes || [])];
                  
                  // Sort data using the new sort function
                  processedData = sortData(processedData, adTypeSort);
                  
                  // Limit items
                  processedData = processedData.slice(0, adTypeTableConfig.maxItems);
                  
                  // Render based on view mode
                  switch (adTypeTableConfig.viewMode) {
                    case 'table':
                      return (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-slate-700">
                                <th 
                                  className="text-left py-2 text-slate-400 font-medium cursor-pointer hover:text-purple-300 transition-colors"
                                  onClick={() => setAdTypeSort(prev => ({
                                    field: 'type',
                                    order: prev.field === 'type' && prev.order === 'asc' ? 'desc' : 'asc'
                                  }))}
                                >
                                  Ad Type {adTypeSort.field === 'type' && (adTypeSort.order === 'asc' ? '↑' : '↓')}
                                </th>
                                {adTypeTableConfig.showMetrics.includes('count') && (
                                  <th 
                                    className="text-right py-2 text-slate-400 font-medium cursor-pointer hover:text-purple-300 transition-colors"
                                    onClick={() => setAdTypeSort(prev => ({
                                      field: 'count',
                                      order: prev.field === 'count' && prev.order === 'asc' ? 'desc' : 'asc'
                                    }))}
                                  >
                                    Count {adTypeSort.field === 'count' && (adTypeSort.order === 'asc' ? '↑' : '↓')}
                                  </th>
                                )}
                                {adTypeTableConfig.showMetrics.includes('spend') && (
                                  <th 
                                    className="text-right py-2 text-slate-400 font-medium cursor-pointer hover:text-purple-300 transition-colors"
                                    onClick={() => setAdTypeSort(prev => ({
                                      field: 'spend',
                                      order: prev.field === 'spend' && prev.order === 'asc' ? 'desc' : 'asc'
                                    }))}
                                  >
                                    Spend {adTypeSort.field === 'spend' && (adTypeSort.order === 'asc' ? '↑' : '↓')}
                                  </th>
                                )}
                                {adTypeTableConfig.showMetrics.includes('impressions') && (
                                  <th 
                                    className="text-right py-2 text-slate-400 font-medium cursor-pointer hover:text-purple-300 transition-colors"
                                    onClick={() => setAdTypeSort(prev => ({
                                      field: 'impressions',
                                      order: prev.field === 'impressions' && prev.order === 'asc' ? 'desc' : 'asc'
                                    }))}
                                  >
                                    Impressions {adTypeSort.field === 'impressions' && (adTypeSort.order === 'asc' ? '↑' : '↓')}
                                  </th>
                                )}
                                {adTypeTableConfig.showMetrics.includes('ctr') && (
                                  <th 
                                    className="text-right py-2 text-slate-400 font-medium cursor-pointer hover:text-purple-300 transition-colors"
                                    onClick={() => setAdTypeSort(prev => ({
                                      field: 'ctr',
                                      order: prev.field === 'ctr' && prev.order === 'asc' ? 'desc' : 'asc'
                                    }))}
                                  >
                                    CTR {adTypeSort.field === 'ctr' && (adTypeSort.order === 'asc' ? '↑' : '↓')}
                                  </th>
                                )}
                                {adTypeTableConfig.showMetrics.includes('roas') && (
                                  <th 
                                    className="text-right py-2 text-slate-400 font-medium cursor-pointer hover:text-purple-300 transition-colors"
                                    onClick={() => setAdTypeSort(prev => ({
                                      field: 'roas',
                                      order: prev.field === 'roas' && prev.order === 'asc' ? 'desc' : 'asc'
                                    }))}
                                  >
                                    ROAS {adTypeSort.field === 'roas' && (adTypeSort.order === 'asc' ? '↑' : '↓')}
                                  </th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {processedData.map((adType, index) => (
                                <tr 
                                  key={index} 
                                  className="border-b border-slate-700/30 hover:bg-slate-700/30 cursor-pointer transition-colors"
                                  onClick={() => handleMetricClick(`ad_type_${index}`, adType)}
                                >
                                  <td className="py-3 text-white font-medium break-words max-w-xs">{adType.type}</td>
                                  {adTypeTableConfig.showMetrics.includes('count') && (
                                    <td className="py-3 text-right text-slate-300">{adType.count}</td>
                                  )}
                                  {adTypeTableConfig.showMetrics.includes('spend') && (
                                    <td className="py-3 text-right text-slate-300">{adType.spend}</td>
                                  )}
                                  {adTypeTableConfig.showMetrics.includes('impressions') && (
                                    <td className="py-3 text-right text-slate-300">{adType.impressions}</td>
                                  )}
                                  {adTypeTableConfig.showMetrics.includes('ctr') && (
                                    <td className="py-3 text-right text-slate-300">{adType.ctr}</td>
                                  )}
                                  {adTypeTableConfig.showMetrics.includes('roas') && (
                                    <td className="py-3 text-right text-slate-300">{adType.roas}</td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    

                    
                    default: // cards view
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {processedData.map((adType, index) => (
                            <div key={index} 
                                 className="p-4 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-all duration-200 cursor-pointer group"
                                 onClick={() => handleMetricClick(`ad_type_${index}`, adType)}>
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-3">
                                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: adType.color }}>
                                    <BarChart3 className="h-5 w-5 text-white" />
                                  </div>
                                  <h4 className="font-medium text-white group-hover:text-purple-300 transition-colors break-words line-clamp-2">{adType.type}</h4>
                                </div>
                                <ChevronRight className="h-4 w-4 text-slate-500 group-hover:text-purple-400 transition-colors flex-shrink-0" />
                              </div>
                              <div className="space-y-2">
                                {adTypeTableConfig.showMetrics.includes('count') && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-slate-400">Count:</span>
                                    <span className="text-slate-300">{adType.count}</span>
                                  </div>
                                )}
                                {adTypeTableConfig.showMetrics.includes('spend') && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-slate-400">Spend:</span>
                                    <span className="text-slate-300">{adType.spend}</span>
                                  </div>
                                )}
                                {adTypeTableConfig.showMetrics.includes('impressions') && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-slate-400">Impressions:</span>
                                    <span className="text-slate-300">{adType.impressions}</span>
                                  </div>
                                )}
                                {adTypeTableConfig.showMetrics.includes('ctr') && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-slate-400">CTR:</span>
                                    <span className="text-slate-300">{adType.ctr}</span>
                                  </div>
                                )}
                                {adTypeTableConfig.showMetrics.includes('roas') && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-slate-400">ROAS:</span>
                                    <span className="text-slate-300">{adType.roas}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                  }
                })()
              )}
            </div>
          </div>

          {/* Highest Spending Features */}
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6" data-chart-id="features">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Highest Spending Features</h3>
              {!sectionLoading.highestSpendingFeatures && (
                <button
                  onClick={() => setShowFeaturesConfig(true)}
                  className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors text-slate-400 hover:text-purple-300"
                  title="Configure Features Display"
                >
                  <Settings className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="space-y-4">
              {sectionLoading.highestSpendingFeatures ? (
                <Loader2 className="mx-auto my-8 animate-spin text-purple-400" />
              ) : (
                (() => {
                  let processedData = sortData(dashboardData.fastGrowingFeatures || [], featuresSort);
                  
                  // Limit items
                  processedData = processedData.slice(0, featuresTableConfig.maxItems);
                  
                  // Render based on view mode
                  switch (featuresTableConfig.viewMode) {
                    case 'table':
                      return (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-slate-700">
                                <th 
                                  className="text-left py-2 text-slate-400 font-medium cursor-pointer hover:text-purple-300 transition-colors"
                                  onClick={() => setFeaturesSort(prev => ({
                                    field: 'feature',
                                    order: prev.field === 'feature' && prev.order === 'asc' ? 'desc' : 'asc'
                                  }))}
                                >
                                  Feature {featuresSort.field === 'feature' && (featuresSort.order === 'asc' ? '↑' : '↓')}
                                </th>
                                {featuresTableConfig.showMetrics.includes('count') && (
                                  <th 
                                    className="text-right py-2 text-slate-400 font-medium cursor-pointer hover:text-purple-300 transition-colors"
                                    onClick={() => setFeaturesSort(prev => ({
                                      field: 'count',
                                      order: prev.field === 'count' && prev.order === 'asc' ? 'desc' : 'asc'
                                    }))}
                                  >
                                    Count {featuresSort.field === 'count' && (featuresSort.order === 'asc' ? '↑' : '↓')}
                                  </th>
                                )}
                                {featuresTableConfig.showMetrics.includes('spend') && (
                                  <th 
                                    className="text-right py-2 text-slate-400 font-medium cursor-pointer hover:text-purple-300 transition-colors"
                                    onClick={() => setFeaturesSort(prev => ({
                                      field: 'spend',
                                      order: prev.field === 'spend' && prev.order === 'asc' ? 'desc' : 'asc'
                                    }))}
                                  >
                                    Spend {featuresSort.field === 'spend' && (featuresSort.order === 'asc' ? '↑' : '↓')}
                                  </th>
                                )}
                                {featuresTableConfig.showMetrics.includes('impressions') && (
                                  <th 
                                    className="text-right py-2 text-slate-400 font-medium cursor-pointer hover:text-purple-300 transition-colors"
                                    onClick={() => setFeaturesSort(prev => ({
                                      field: 'impressions',
                                      order: prev.field === 'impressions' && prev.order === 'asc' ? 'desc' : 'asc'
                                    }))}
                                  >
                                    Impressions {featuresSort.field === 'impressions' && (featuresSort.order === 'asc' ? '↑' : '↓')}
                                  </th>
                                )}
                                {featuresTableConfig.showMetrics.includes('ctr') && (
                                  <th 
                                    className="text-right py-2 text-slate-400 font-medium cursor-pointer hover:text-purple-300 transition-colors"
                                    onClick={() => setFeaturesSort(prev => ({
                                      field: 'ctr',
                                      order: prev.field === 'ctr' && prev.order === 'asc' ? 'desc' : 'asc'
                                    }))}
                                  >
                                    CTR {featuresSort.field === 'ctr' && (featuresSort.order === 'asc' ? '↑' : '↓')}
                                  </th>
                                )}
                                {featuresTableConfig.showMetrics.includes('roas') && (
                                  <th 
                                    className="text-right py-2 text-slate-400 font-medium cursor-pointer hover:text-purple-300 transition-colors"
                                    onClick={() => setFeaturesSort(prev => ({
                                      field: 'roas',
                                      order: prev.field === 'roas' && prev.order === 'asc' ? 'desc' : 'asc'
                                    }))}
                                  >
                                    ROAS {featuresSort.field === 'roas' && (featuresSort.order === 'asc' ? '↑' : '↓')}
                                  </th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {processedData.map((feature, index) => (
                                <tr 
                                  key={index} 
                                  className="border-b border-slate-700/30 hover:bg-slate-700/30 cursor-pointer transition-colors"
                                  onClick={() => handleMetricClick(`feature_${index}`, feature)}
                                >
                                  <td className="py-3 text-white font-medium break-words max-w-xs">{feature.feature}</td>
                                  {featuresTableConfig.showMetrics.includes('count') && (
                                    <td className="py-3 text-right text-slate-300">{feature.count}</td>
                                  )}
                                  {featuresTableConfig.showMetrics.includes('spend') && (
                                    <td className="py-3 text-right text-slate-300">{feature.spend}</td>
                                  )}
                                  {featuresTableConfig.showMetrics.includes('impressions') && (
                                    <td className="py-3 text-right text-slate-300">{feature.impressions}</td>
                                  )}
                                  {featuresTableConfig.showMetrics.includes('ctr') && (
                                    <td className="py-3 text-right text-slate-300">{feature.ctr}</td>
                                  )}
                                  {featuresTableConfig.showMetrics.includes('roas') && (
                                    <td className="py-3 text-right text-slate-300">{feature.roas}</td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    
                    default: // cards view
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {processedData.map((feature, index) => (
                            <div key={index} 
                                 className="p-4 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-all duration-200 cursor-pointer group"
                                 onClick={() => handleMetricClick(`feature_${index}`, feature)}>
                                                             <div className="flex items-center justify-between mb-3">
                                 <h4 className="font-medium text-white group-hover:text-purple-300 transition-colors break-words line-clamp-2 flex-1 mr-2">{feature.feature}</h4>
                                 <ChevronRight className="h-4 w-4 text-slate-500 group-hover:text-purple-400 transition-colors flex-shrink-0" />
                               </div>
                              <div className="space-y-2">
                                {featuresTableConfig.showMetrics.includes('count') && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-slate-400">Count:</span>
                                    <span className="text-slate-300">{feature.count}</span>
                                  </div>
                                )}
                                {featuresTableConfig.showMetrics.includes('spend') && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-slate-400">Spend:</span>
                                    <span className="text-slate-300">{feature.spend}</span>
                                  </div>
                                )}
                                {featuresTableConfig.showMetrics.includes('impressions') && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-slate-400">Impressions:</span>
                                    <span className="text-slate-300">{feature.impressions}</span>
                                  </div>
                                )}
                                {featuresTableConfig.showMetrics.includes('ctr') && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-slate-400">CTR:</span>
                                    <span className="text-slate-300">{feature.ctr}</span>
                                  </div>
                                )}
                                {featuresTableConfig.showMetrics.includes('roas') && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-slate-400">ROAS:</span>
                                    <span className="text-slate-300">{feature.roas}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                  }
                })()
              )}
            </div>
          </div>

          {/* Top Performing Content */}
          <div className={`backdrop-blur border rounded-xl p-6 ${
            isTopPerformingConfigured 
              ? 'bg-slate-800/50 border-slate-700/50' 
              : dashboardData.isCustomTopContent 
                ? 'bg-purple-900/30 border-purple-600/50' 
                : 'bg-slate-800/50 border-slate-700/50'
          }`} data-chart-id="top_content">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div>
                  <div className="flex items-center space-2">
                    <h3 className="text-xl font-semibold text-white">
                      {(() => {
                        // If section is configured, use the configured query as title
                        if (isTopPerformingConfigured && configuredTopContentQuery) {
                          return configuredTopContentQuery;
                        }
                        
                        // Otherwise, use static title - no dynamic behavior
                        return 'Top Performing Ads';
                      })()}
                    </h3>
                    {isTopPerformingConfigured && (
                      <span className="px-2 py-1 text-xs bg-green-600/50 text-green-200 rounded-full border border-green-500/50">
                        Configured
                      </span>
                    )}
                    {!isTopPerformingConfigured && dashboardData.isCustomTopContent && (
                      <span className="px-2 py-1 text-xs bg-purple-600/50 text-purple-200 rounded-full border border-purple-500/50">
                        Custom Query
                      </span>
                    )}
                  </div>
                  {(() => {
                    // Only show subtitle when section is user-configured
                    if (isTopPerformingConfigured && configuredTopContentQuery) {
                      return (
                        <p className="text-sm text-slate-400 mt-1">
                          Query: "{configuredTopContentQuery}"
                        </p>
                      );
                    }
                    
                    // No dynamic subtitle - only show when user configures it
                    return null;
                  })()}
                </div>

              </div>
              {!sectionLoading.topContent && (
                <button
                  onClick={() => {
                    // Set the flag immediately when modal opens to prevent timing issues
                    setIsTopPerformingConfigured(true);
                    setShowContentConfig(true);
                  }}
                  className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors text-slate-400 hover:text-purple-300"
                  title={isTopPerformingConfigured ? "Reconfigure Top Performing Section" : "Configure Content Display"}
                >
                  <Settings className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="space-y-4">
              {sectionLoading.topContent ? (
                <Loader2 className="mx-auto my-8 animate-spin text-purple-400" />
              ) : (
                (() => {
                  // Check if we have data
                  if (!dashboardData.topContent || dashboardData.topContent.length === 0) {
                    return (
                      <div className="text-center py-12 text-slate-400">
                        <div className="mb-4">
                          <TrendingUp className="h-12 w-12 mx-auto text-slate-600" />
                        </div>
                        <h4 className="text-lg font-medium text-slate-300 mb-2">No Top Performing Content Available</h4>
                        <p className="text-sm text-slate-500 mb-4">
                          The current filters returned no results. Try adjusting your date range or ad name filters.
                        </p>
                        <div className="flex items-center justify-center space-x-2 text-xs text-slate-500">
                          <span>💡 Tip:</span>
                          <span>Use broader date ranges or remove ad name filters to see more data</span>
                        </div>
                      </div>
                    );
                  }
                  
                  // Create dynamic table structure first
                  const dynamicTable = createDynamicTable(dashboardData.topContent || []);
                  
                  console.log('🔍 Dynamic table structure:', {
                    columns: dynamicTable.columns.map(col => ({ key: col.key, label: col.label, type: col.type })),
                    sampleData: dashboardData.topContent?.slice(0, 2),
                    sortConfig: contentSort,
                    allKeys: dashboardData.topContent?.[0] ? Object.keys(dashboardData.topContent[0]) : [],
                    filteredKeys: dynamicTable.columns.map(col => col.key)
                  });
                  


                  // Simple sorting like Ad Types section - no complex dynamic logic
                  let processedData = [...(dashboardData.topContent || [])];
                  
                  // Sort data using the sort function (same pattern as Ad Types)
                  processedData = sortData(processedData, contentSort);
                  
                  // Limit items
                  processedData = processedData.slice(0, contentTableConfig.maxItems);
                  
                  // Render based on view mode
                  switch (contentTableConfig.viewMode) {
                    case 'table':
                      return (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-slate-700">
                                {dynamicTable.columns.map((column) => (
                                  <th 
                                    key={column.key}
                                    className={`py-2 text-slate-400 font-medium cursor-pointer hover:text-purple-300 transition-colors ${
                                      column.type === 'text' ? 'text-left' : 'text-right'
                                    }`}
                                    onClick={() => setContentSort(prev => ({
                                      field: column.key,
                                      order: prev.field === column.key && prev.order === 'asc' ? 'desc' : 'asc'
                                    }))}
                                  >
                                    {column.label} {contentSort.field === column.key && (contentSort.order === 'asc' ? '↑' : '↓')}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {processedData.map((content, index) => (
                                <tr 
                                  key={`${contentSort.field}-${contentSort.order}-${index}`} 
                                  className="border-b border-slate-700/30 hover:bg-slate-700/30 cursor-pointer transition-colors"
                                  onClick={() => handleMetricClick(`content_${index}`, content)}
                                >
                                  {dynamicTable.columns.map((column) => (
                                    <td 
                                      key={column.key}
                                      className={`py-3 ${
                                        column.type === 'text' ? 'text-white font-medium break-words max-w-xs' : 'text-right text-slate-300'
                                      }`}
                                    >
                                      {formatValue(content[column.key], column.type)}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    
                    default: // cards view
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {processedData.map((content, index) => (
                            <div key={index} 
                                 className="p-4 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-all duration-200 cursor-pointer group"
                                 onClick={() => handleMetricClick(`content_${index}`, content)}>
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-medium text-white group-hover:text-purple-300 transition-colors break-words line-clamp-2 flex-1 mr-2">
                                  {formatValue(content[dynamicTable.columns[0]?.key], 'text')}
                                </h4>
                                <ChevronRight className="h-4 w-4 text-slate-500 group-hover:text-purple-400 transition-colors flex-shrink-0" />
                              </div>
                              <div className="space-y-2">
                                {dynamicTable.columns.slice(1).map((column) => (
                                  <div key={column.key} className="flex justify-between text-sm">
                                    <span className="text-slate-400">{column.label}:</span>
                                    <span className="text-slate-300">{formatValue(content[column.key], column.type)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                  }
                })()
              )}
            </div>
          </div>
            </>
          )}
        </div>

        {/* AI Chat Assistant */}
        <div className={`fixed right-0 top-20 bottom-0 bg-slate-800/90 backdrop-blur border-l border-slate-700/50 transition-all duration-100 z-20 ${
          chatSize === 'large' ? 'w-1/2 xl:w-1/2 lg:w-3/5 md:w-7/10 sm:w-full' : 
          chatSize === 'small' ? 'w-96 xl:w-96 lg:w-80 md:w-64 sm:w-full' : 
          chatSize === 'custom' ? `w-[${customChatWidth}px]` :
          'w-20 sm:w-16'
        } flex flex-col ${isResizing ? 'shadow-2xl' : ''} ${
          // Add mobile-specific styles - keep top-20 even on mobile
          chatSize !== 'collapsed' ? 'sm:border-l-0 sm:border-t sm:shadow-2xl' : ''
        }`} style={chatSize === 'custom' ? { width: `${customChatWidth}px` } : {}}>
          {/* Resize Handle */}
          {chatSize !== 'collapsed' && (
            <div 
              className={`absolute left-0 top-0 bottom-0 w-2 bg-slate-600/50 hover:bg-purple-500/50 cursor-ew-resize transition-all duration-75 z-10 ${
                isResizing ? 'bg-purple-500/80 w-4 shadow-lg' : ''
              }`}
              onMouseDown={handleResizeStart}
              title="Drag to resize chat window"
            >
                              <div className={`absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-all duration-75 ${
                  isResizing ? 'opacity-100 scale-125' : 'opacity-0 hover:opacity-100'
                }`}>
                  <GripVertical className={`h-4 w-4 text-slate-400 transition-colors duration-75 ${
                    isResizing ? 'text-purple-300' : ''
                  }`} />
                </div>
              {/* Enhanced visual feedback during resize */}
              {isResizing && (
                <>
                  <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-purple-400/60" />
                  <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-purple-500/20 rounded-full border border-purple-400/40" />
                </>
              )}
            </div>
          )}
          
          {/* Chat Header */}
          <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
            {chatSize !== 'collapsed' ? (
              <>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Creative AI Assistant</h3>
                    <p className="text-xs text-slate-400">You can minimize this chat window to focus on your dashboard data</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {/* Large expand button */}
                  <button 
                    onClick={() => setChatSize(chatSize === 'large' ? 'small' : 'large')}
                    className={`p-2 rounded-lg transition-colors ${
                      chatSize === 'large' 
                        ? 'bg-purple-500 text-white' 
                        : 'hover:bg-slate-700/50 text-slate-400'
                    }`}
                    title={chatSize === 'large' ? 'Switch to small' : 'Switch to large'}
                  >
                    <Maximize2 className="h-4 w-4" />
                  </button>
                  {/* Small size button */}
                  <button 
                    onClick={() => setChatSize('small')}
                    className={`p-2 rounded-lg transition-colors ${
                      chatSize === 'small' 
                        ? 'bg-purple-500 text-white' 
                        : 'hover:bg-slate-700/50 text-slate-400'
                    }`}
                    title="Small size"
                  >
                    <div className="w-4 h-4 border-2 border-current rounded"></div>
                  </button>
                  {/* Minimize button */}
                  <button 
                    onClick={() => setChatSize('collapsed')}
                    className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                    title="Minimize chat"
                  >
                    <Minimize className="h-4 w-4 text-slate-400" />
                  </button>
                </div>
              </>
            ) : (
              <button 
                onClick={() => setChatSize('small')}
                className="w-full p-2 hover:bg-slate-700/50 rounded-lg transition-colors group"
                title="Open chat"
              >
                <MessageCircle className="h-6 w-6 text-purple-400 mx-auto group-hover:scale-110 transition-transform" />
              </button>
            )}
          </div>

          {chatSize !== 'collapsed' && (
            <>
              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {chatMessages.map((message) => (
                  <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] p-3 rounded-lg break-words group ${
                      message.role === 'user' 
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' 
                        : 'bg-slate-700/50 text-slate-100 border border-slate-600/50'
                    }`}>
                      <div className="flex items-start space-x-2">
                        {message.role === 'assistant' && (
                          <Bot className="h-4 w-4 text-purple-400 mt-0.5 flex-shrink-0" />
                        )}
                        {message.role === 'user' && (
                          <User className="h-4 w-4 text-white mt-0.5 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          {editingMessageId === message.id ? (
                            <div className="space-y-2 -mx-4 -my-2 p-4 bg-slate-800/50 rounded-lg">
                              <textarea
                                value={editingText}
                                onChange={(e) => {
                                  setEditingText(e.target.value);
                                  // Auto-resize to fit content
                                  e.target.style.height = 'auto';
                                  e.target.style.height = e.target.scrollHeight + 'px';
                                }}
                                className="w-full p-4 text-sm bg-slate-800 border border-slate-600 rounded text-white resize-none overflow-hidden"
                                style={{
                                  height: 'auto',
                                  minHeight: '60px',
                                  width: '100%',
                                  minWidth: '500px'
                                }}
                                autoFocus
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={handleSaveEdit}
                                  className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded"
                                >
                                  Save & Rerun
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="px-3 py-1 bg-slate-600 hover:bg-slate-700 text-white text-xs rounded"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div 
                                className="text-sm leading-relaxed break-words prose prose-invert prose-sm max-w-none"
                                dangerouslySetInnerHTML={{
                                  __html: (() => {
                                    let content = message.content;
                                    
                                    // First, process markdown links to avoid conflicts
                                    content = content.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-400 hover:text-blue-300 underline decoration-dotted underline-offset-2">$1</a>');
                                    
                                    // Then process raw URLs (but not those already in links)
                                    content = content.replace(/(?<!href=")(https?:\/\/[^\s\)]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-400 hover:text-blue-300 underline decoration-dotted underline-offset-2">View Asset</a>');
                                    
                                    // Then process other markdown
                                    content = content
                                      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold text-white mb-2 mt-4">$1</h3>')
                                      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold text-white mb-3 mt-5">$1</h2>')
                                      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold text-white mb-4 mt-6">$1</h1>')
                                      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-purple-300">$1</strong>')
                                      .replace(/\*(.*?)\*/g, '<em class="italic text-slate-300">$1</em>')
                                      .replace(/`(.*?)`/g, '<code class="bg-slate-700 px-1 py-0.5 rounded text-xs font-mono text-green-300">$1</code>')
                                      .replace(/^\- (.*$)/gim, '<li class="ml-4 mb-1">• $1</li>')
                                      .replace(/^\d+\. (.*$)/gim, '<li class="ml-4 mb-1">$1</li>')
                                      .replace(/\n\n/g, '</p><p class="mb-3">')
                                      .replace(/\n/g, '<br>')
                                      .replace(/^(?!<[h|l])/gm, '<p class="mb-3">')
                                      .replace(/(<li.*<\/li>)/g, '<ul class="list-disc list-inside mb-3 space-y-1">$1</ul>')
                                      .replace(/<ul class="list-disc list-inside mb-3 space-y-1"><ul class="list-disc list-inside mb-3 space-y-1">/g, '<ul class="list-disc list-inside mb-3 space-y-1">')
                                      .replace(/<\/ul><\/ul>/g, '</ul>');
                                    
                                    return content;
                                  })()
                                }}
                              />
                              
                              {/* Edit button for user messages */}
                              {message.role === 'user' && (
                                <button
                                  onClick={() => handleEditMessage(message.id, message.content)}
                                  className="mt-2 text-xs text-purple-300 hover:text-purple-200 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  ✏️ Edit & Rerun
                                </button>
                              )}
                            </>
                          )}
                          
                          {/* Display error suggestions if available */}
                          {message.error && message.suggestions && message.suggestions.length > 0 && (
                            <div className="mt-3 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
                              <p className="text-red-300 text-xs font-medium mb-2">Suggestions:</p>
                              <ul className="space-y-1">
                                {message.suggestions.map((suggestion, index) => (
                                  <li key={index} className="text-red-200 text-xs flex items-start gap-2">
                                    <span className="text-red-400">•</span>
                                    <span>{suggestion}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Display suggested prompts if available */}
                          {message.suggestedPrompts && (
                            <div className="mt-3 space-y-2">
                              {message.suggestedPrompts.map((prompt, index) => (
                                <button
                                  key={index}
                                  onClick={() => {
                                    // Only replace input if it's empty or just whitespace
                                    if (!inputMessage.trim()) {
                                      setInputMessage(prompt.text);
                                    } else {
                                      // If user has typed something, append the prompt
                                      setInputMessage(prev => prev + (prev.endsWith(' ') ? '' : ' ') + prompt.text);
                                    }
                                    // Auto-focus the input
                                    setTimeout(() => {
                                      const input = document.querySelector('textarea[placeholder*="Ask about your Meta ads"]');
                                      if (input) input.focus();
                                    }, 100);
                                  }}
                                  className="block w-full text-left text-xs bg-slate-600/30 hover:bg-slate-500/40 text-slate-200 hover:text-white p-3 rounded-lg hover:shadow-md transition-all duration-200 border border-slate-500/30 hover:border-slate-400/50"
                                >
                                  <div className="font-medium text-purple-300 mb-1">"{prompt.text}"</div>
                                  <div className="text-slate-400 text-xs">{prompt.description}</div>
                                </button>
                              ))}
                            </div>
                          )}
                          
                          {/* Display SQL query if available (collapsible) */}
                          {message.sqlQuery && (
                            <div className="mt-3">
                              <details className="group">
                                <summary className="cursor-pointer text-xs text-slate-400 hover:text-purple-300 flex items-center space-x-1 transition-colors">
                                  <ChevronRight className="h-3 w-3 group-open:rotate-90 transition-transform" />
                                  <span>🔍 View SQL Query (Debug)</span>
                                </summary>
                                <div className="mt-2 p-3 bg-slate-800/50 border border-slate-600/30 rounded-lg">
                                  <pre className="text-xs text-slate-300 font-mono whitespace-pre-wrap overflow-x-auto">{message.sqlQuery}</pre>
                                </div>
                              </details>
                            </div>
                          )}
                          
                          {/* Display smart visualization if available */}
                          {message.data && message.columns && (
                            <div className="mt-3">
                                                <SmartVisualization 
                    data={message.data} 
                    columns={message.columns}
                    title={message.visualizationTitle || "Query Results"}
                    description={message.visualizationDescription}
                  />
                            </div>
                          )}
                          
                          {/* Display asset grid if available */}
                          {message.assetUrls && message.assetUrls.length > 0 && (
                            <div className="mt-3">
                              <AssetGrid 
                                assets={message.assetUrls.map(asset => ({
                                  ...asset,
                                  metrics: asset.metrics || {},
                                  title: asset.title || 'Ad Asset'
                                }))}
                                title="Ad Assets with Performance"
                              />
                            </div>
                          )}

                          {/* Display follow-up questions if available */}
                          {message.followUpQuestions && message.followUpQuestions.length > 0 && (
                            <div className="mt-4 pt-3 border-t border-slate-600/50">
                              <p className="text-xs text-slate-400 mb-2">💡 Suggested follow-up questions:</p>
                              <div className="space-y-2">
                                {message.followUpQuestions.map((question, index) => (
                                  <button
                                    key={index}
                                    onClick={() => {
                                      // Only replace input if it's empty or just whitespace
                                      if (!inputMessage.trim()) {
                                        setInputMessage(question);
                                      } else {
                                        // If user has typed something, append the question
                                        setInputMessage(prev => prev + (prev.endsWith(' ') ? '' : ' ') + question);
                                      }
                                      // Auto-focus the input
                                      setTimeout(() => {
                                        const input = document.querySelector('input[type="text"]');
                                        if (input) input.focus();
                                      }, 100);
                                    }}
                                    className="block w-full text-left text-xs text-slate-300 hover:text-purple-300 p-2 rounded-lg hover:bg-slate-600/30 transition-colors"
                                  >
                                    {question}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-slate-700/50 border border-slate-600/50 p-3 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Bot className="h-4 w-4 text-purple-400" />
                        <span className="text-sm text-slate-300">AI is analyzing your data... {Math.round(chatLoadingProgress)}%</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Chat Input */}
              <div className="p-4 border-t border-slate-700/50">
                {isTextboxExpanded && (
                  <div className="mb-2 text-xs text-slate-400 flex items-center space-x-2">
                    <span>📝 Expanded mode active</span>
                    <span>•</span>
                    <span>Press Enter to send, Shift+Enter for new line</span>
                    <button 
                      onClick={() => setIsTextboxExpanded(false)}
                      className="text-purple-400 hover:text-purple-300 ml-auto"
                    >
                      Collapse ↕️
                    </button>
                  </div>
                )}
                <div className="flex space-x-2">
                  <textarea
                    value={inputMessage}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about your Meta ads... (Shift+Enter for new line)"
                    rows={isTextboxExpanded ? 6 : 1}
                    className={`flex-1 bg-slate-700/50 border border-slate-600/50 rounded-lg px-3 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 transition-all resize-none ${
                      isTextboxExpanded ? 'min-h-[140px]' : 'min-h-[40px]'
                    }`}
                    style={{ 
                      overflow: isTextboxExpanded ? 'auto' : 'hidden',
                      lineHeight: '1.5',
                      whiteSpace: 'pre-wrap'
                    }}
                  />
                  <div className="flex space-x-1">
                    {!isTextboxExpanded && (
                      <button
                        onClick={() => setIsTextboxExpanded(true)}
                        className="bg-slate-600/50 hover:bg-slate-500/50 p-2 rounded-lg transition-all duration-200"
                        title="Expand for longer messages"
                      >
                        <Maximize2 className="h-4 w-4 text-slate-300" />
                      </button>
                    )}
                    <button
                      onClick={handleSendMessage}
                      disabled={!inputMessage.trim() || isTyping}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed p-2 rounded-lg transition-all duration-200 transform hover:scale-105"
                    >
                      {isTyping ? <Loader2 className="h-4 w-4 text-white animate-spin" /> : <Send className="h-4 w-4 text-white" />}
                    </button>
                  </div>
                </div>
                

              </div>
            </>
          )}
        </div>
      </div>


      
      {/* Dashboard Configuration Modal */}
      <DashboardConfig
        isOpen={showConfig}
        onClose={() => setShowConfig(false)}
        onSave={handleConfigSave}
        currentConfig={dashboardConfig}
      />
      
      {/* Toast Notifications */}
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#1e293b',
            color: '#f8fafc',
            border: '1px solid #475569',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#f8fafc',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#f8fafc',
            },
          },
          loading: {
            duration: 5000,
            iconTheme: {
              primary: '#8b5cf6',
              secondary: '#f8fafc',
            },
          },
        }}
      />
      <AdTypeConfigModal />
      <FeaturesConfigModal />
      <ContentConfigModal />
      
      {/* Portal-based popup for settings icon warning */}
      {isLoading || !dataLoaded ? (
        createPortal(
          <div 
            className={`fixed px-3 py-2 bg-slate-900 text-white text-xs rounded-lg whitespace-nowrap z-[9999] border border-slate-700 shadow-lg transition-opacity duration-200 pointer-events-none ${
              settingsIconRef.current && document.querySelector('.group.relative:hover') ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              top: settingsIconRef.current ? settingsIconRef.current.getBoundingClientRect().bottom + 8 : 0,
              left: settingsIconRef.current ? settingsIconRef.current.getBoundingClientRect().left + settingsIconRef.current.getBoundingClientRect().width / 2 : 0,
              transform: 'translateX(-50%)'
            }}
          >
            No config changes during dashboard loading
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-slate-900"></div>
          </div>,
          document.body
        )
      ) : null}
      
      {/* Save Report Modal */}
      {showSaveReportModal && createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Save Report</h3>
              <button
                onClick={() => {
                  setShowSaveReportModal(false);
                  setNewReportName('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Save your current filter configuration for quick access later.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Report Name
                </label>
                <input
                  type="text"
                  value={newReportName}
                  onChange={(e) => setNewReportName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveReport();
                    }
                  }}
                  placeholder="e.g., Last 7 Days High ROAS"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  autoFocus
                />
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <div className="font-medium text-gray-700 mb-2">Current Configuration:</div>
                <ul className="space-y-1 text-gray-600">
                  <li className="flex items-center gap-2">
                    📋 Report Type: 
                    <span className="font-medium">
                      {REPORT_TYPE_NAMES[activeDashboardTab] || activeDashboardTab}
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    📅 Date Range: 
                    {dateRangeType === 'custom' && startDate && endDate ? (
                      (() => {
                        // Extract YYYY-MM-DD part from any date format
                        const startDateStr = typeof startDate === 'string' ? startDate.split('T')[0] : startDate.toISOString().split('T')[0];
                        const endDateStr = typeof endDate === 'string' ? endDate.split('T')[0] : endDate.toISOString().split('T')[0];
                        const start = new Date(startDateStr + 'T00:00:00');
                        const end = new Date(endDateStr + 'T00:00:00');
                        return (
                          <span className="font-medium">
                            {start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        );
                      })()
                    ) : (
                      <span className="font-medium">{selectedRange} days</span>
                    )}
                  </li>
                  <li>📊 Metric: {correlationMetric.toUpperCase()}</li>
                  {adNameFilter.keywords.length > 0 && adNameFilter.keywords.some(k => k.trim()) && (
                    <li className="flex items-center gap-2">
                      <span className={adNameFilter.mode === 'exclude' ? 'text-red-500' : 'text-green-600'}>
                        {adNameFilter.mode === 'exclude' ? '❌' : '✅'}
                      </span>
                      <span className="font-medium">{adNameFilter.mode === 'exclude' ? 'Exclude' : 'Include'}:</span>
                      {adNameFilter.keywords.filter(k => k.trim()).join(', ')}
                      {adNameFilter.keywords.filter(k => k.trim()).length > 1 && (
                        <span className="text-gray-500">({adNameFilter.logic})</span>
                      )}
                    </li>
                  )}
                </ul>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowSaveReportModal(false);
                    setNewReportName('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveReport}
                  disabled={!newReportName.trim()}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Save Report
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Dashboard; 